import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin, Switch } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { getEmployeesAPI } from "../../../Redux/Apis/employeeApi";
import {
  checkPermission,
  hasAdminAccess,
  isCeoOrCmoRoleUser,
  permissionType,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import RemoveSalaryStructureModal from "../../molecules/RemoveSalaryStructureModal/RemoveSalaryStructureModal";
import {
  PAID_LEAVES_PER_YEAR,
  MONTHLY_PAID_LEAVE_ACCRUAL,
  PROFESSIONAL_TAX_MONTHLY,
  MONTH_OPTIONS,
  buildSheetSummary,
  calculateGrossFromStructure,
  formatCurrency,
  getSalaryStructureBreakdown,
  getSheetDisplayBasicSalary,
  isEntryProfessionalTaxEnabled,
  isTraineeDesignation,
  getMonthLabel,
  getWorkingDaysInCalendarMonth,
  getDefaultFinancePeriod,
  getFinanceMonthOptions,
  getFinancePeriodStatus,
  getFinanceYearOptions,
  FINANCE_SHEET_START,
  MANUAL_SHEET_MIN,
  canCreateManualFinanceSheet,
  isManualFinanceSheetPeriod,
  canDeleteSalarySheetForPeriod,
  isEligibleForAutoTds,
  getSalaryEntryStatusBadgeClass,
  getSalaryEntryStatusLabel,
  getSalaryEntryStatusSelectClass,
  isSalaryEntryPaid,
  normalizeSalaryEntryStatus,
  SALARY_ENTRY_STATUS,
  SALARY_ENTRY_STATUS_OPTIONS,
  syncLeaveAndWorkedDaysForSheetEntry,
  toNumber,
} from "../../../app/utils/finance/salaryCalculations";
import {
  DEFAULT_SALARY_COMPANY_NAME,
  financeCompaniesForTabs,
  getTheCodingStudioTabId,
  isTheCodingStudioTabActive,
  matchesActiveSalaryCompanyTab,
  resolveSalaryCompanyIdForActiveTab,
} from "../../../app/utils/company/financeDefaultSalaryCompany";
import {
  FINANCE_SALARY_COMPANIES_UPDATED_EVENT,
  getFinanceSalaryCompanyLabelSync,
  readFinanceSalaryCompanies,
} from "../../../app/utils/company/financeSalaryCompaniesStorage";
import CompanyDetailsFormModal from "../../molecules/CompanyDetailsFormModal/CompanyDetailsFormModal";
import {
  downloadManualSheetImportTemplate,
  parseManualSheetImportFile,
} from "../../../app/utils/finance/manualSheetImport";
import {
  fetchFinanceDocumentInvoiceBlob,
  fetchFinanceDocumentUpiScreenshotBlob,
  isImageMimeType,
  isPdfMimeType,
} from "../../../app/utils/common/financeDocumentFile";
import {
  fetchExpenseInvoiceBlob,
  fetchExpenseUpiScreenshotBlob,
} from "../../../app/utils/common/financeExpenseFile";
import {
  EXPENSE_FILE_ACCEPT,
  EXPENSE_IMAGE_ACCEPT,
  EXPENSE_TYPE_OPTIONS,
  EXPENSE_TYPE_OTHER,
  filterExpensesBySection,
  getExpensePeriodFromDate,
  getExpenseTypeLabel,
  getExpenseTypeOptionsForSection,
  getTodayInputValue,
  isOfficeExpenseType,
  PAYMENT_METHOD_OPTIONS,
} from "../../../app/utils/finance/expenseConstants";
import CompanyPolicyPdfViewer from "../../molecules/CompanyPolicyPdfViewer/CompanyPolicyPdfViewer";
import {
  SALARY_MANAGEMENT_TABS,
  FINANCE_OVERVIEW_TABS,
  canReadFinanceManagementTab,
  getFinanceManagementTabFromPath,
  getFinanceManagementTabPath,
  getFinancePageSection,
  getFinanceSectionTabs,
  getFinanceTabKeyFromPath,
} from "../../../app/utils/finance/financeManagementTabs";
import {
  EXPENSE_MANAGEMENT_TABS,
  canReadExpenseManagementTab,
  getExpenseManagementTabPath,
  getExpenseSectionFromPath,
} from "../../../app/utils/finance/expenseManagementTabs";
import "./FinanceManagementPage.scss";

const SHEET_COLUMNS = [
  "Sr No",
  "Employees Name",
  "PT",
  "Salary Per Month",
  "Minimum Rate of Salary / Month (Basic)",
  "Designation",
  "Total Working Days",
  "Leaves",
  "Total Worked Days",
  "Leaves Deduction",
  "Basic",
  "Accommodation",
  "Total Gross Earnings",
  "Professional Tax",
  "TDS",
  "Total Deduction",
  "Net Salary",
  "Status",
];

const initialStructureForm = {
  userId: "",
  designation: "",
  monthlySalary: "",
};

const isRemovedStructure = (structure) => Number(structure?.isDelete) === 1;

const getStructureRemovedFromLabel = (lastWorkingDate) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(lastWorkingDate || ""));
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const next = month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
  return `${getMonthLabel(next.month)} ${next.year}`;
};

const defaultFinancePeriod = getDefaultFinancePeriod();
const FinanceManagementPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth.auth);
  const { employees } = useSelector((state) => state.employee);

  const [activeTab, setActiveTab] = useState("sheets");
  const [filterYear, setFilterYear] = useState(defaultFinancePeriod.year);
  const [filterMonth, setFilterMonth] = useState(defaultFinancePeriod.month);
  const [periodMeta, setPeriodMeta] = useState({
    canGenerate: true,
    canCreateManual: false,
    isBeforeStart: false,
    isFuture: false,
  });
  const [manualSheetWorkingDays, setManualSheetWorkingDays] = useState("");
  const [manualSheetCreating, setManualSheetCreating] = useState(false);
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [manualEntrySaving, setManualEntrySaving] = useState(false);
  const [manualEntryMode, setManualEntryMode] = useState("employee");
  const [manualEntryForm, setManualEntryForm] = useState({
    userId: "",
    manualUserId: "",
    employeeName: "",
    monthlySalary: "",
    basicSalary: "",
    accommodation: "",
    designation: "",
    leaveDays: "0",
    leaveDeduction: "0",
    tds: "",
    professionalTaxEnabled: true,
  });
  const [sheetImportEmployees, setSheetImportEmployees] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const bulkImportInputRef = React.useRef(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [loadedPeriodKey, setLoadedPeriodKey] = useState(null);
  const periodLoadRequestRef = useRef(0);

  const [structures, setStructures] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const [showStructureForm, setShowStructureForm] = useState(false);
  const [structureForm, setStructureForm] = useState(initialStructureForm);
  const [editingStructureId, setEditingStructureId] = useState(null);
  const [structureToDelete, setStructureToDelete] = useState(null);
  const [structureDeleteLoading, setStructureDeleteLoading] = useState(false);
  const [sheetToDelete, setSheetToDelete] = useState(null);
  const [sheetDeleteLoading, setSheetDeleteLoading] = useState(false);
  const [companyDataToClear, setCompanyDataToClear] = useState(null);
  const [companyDataClearLoading, setCompanyDataClearLoading] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [entryDeleteLoading, setEntryDeleteLoading] = useState(false);
  const [tdsInputsByEntry, setTdsInputsByEntry] = useState({});
  const [tdsSavingByEntry, setTdsSavingByEntry] = useState({});
  const [ptSavingByEntry, setPtSavingByEntry] = useState({});
  const [statusSavingByEntry, setStatusSavingByEntry] = useState({});
  const [pendingStatusByEntry, setPendingStatusByEntry] = useState({});
  const [activeSalaryCompanyId, setActiveSalaryCompanyId] = useState(() =>
    getTheCodingStudioTabId([])
  );
  const [companyFormOpen, setCompanyFormOpen] = useState(false);
  const [companyProfilesVersion, setCompanyProfilesVersion] = useState(0);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const initialExpenseForm = {
    expenseType: "",
    customExpenseType: "",
    paymentMethod: "",
    upiId: "",
    cardUsedBy: "",
    expenseDate: getTodayInputValue(),
    price: "",
    description: "",
    paidBy: "",
  };
  const [financeDocuments, setFinanceDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsTotalAmount, setDocumentsTotalAmount] = useState(0);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [documentForm, setDocumentForm] = useState(initialExpenseForm);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editingDocumentMeta, setEditingDocumentMeta] = useState(null);
  const [documentInvoiceFile, setDocumentInvoiceFile] = useState(null);
  const [documentUpiScreenshotFile, setDocumentUpiScreenshotFile] = useState(null);
  const documentInvoiceInputRef = useRef(null);
  const documentUpiScreenshotInputRef = useRef(null);
  const [documentSaving, setDocumentSaving] = useState(false);
  const [viewingDocumentAttachment, setViewingDocumentAttachment] = useState(null);
  const [documentAttachmentViewUrl, setDocumentAttachmentViewUrl] = useState(null);
  const [documentAttachmentViewLoading, setDocumentAttachmentViewLoading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [documentDeleteLoading, setDocumentDeleteLoading] = useState(false);
  const [salaryExpenses, setSalaryExpenses] = useState([]);
  const [expensesTotalAmount, setExpensesTotalAmount] = useState(0);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState(initialExpenseForm);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editingExpenseMeta, setEditingExpenseMeta] = useState(null);
  const [expenseInvoiceFile, setExpenseInvoiceFile] = useState(null);
  const [expenseUpiScreenshotFile, setExpenseUpiScreenshotFile] = useState(null);
  const expenseInvoiceInputRef = useRef(null);
  const expenseUpiScreenshotInputRef = useRef(null);
  const [expenseSaving, setExpenseSaving] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [expenseDeleteLoading, setExpenseDeleteLoading] = useState(false);
  const [viewingExpenseAttachment, setViewingExpenseAttachment] = useState(null);
  const [expenseAttachmentViewUrl, setExpenseAttachmentViewUrl] = useState(null);
  const [expenseAttachmentViewLoading, setExpenseAttachmentViewLoading] = useState(false);

  const loadCompanyProfiles = useCallback(async () => {
    try {
      const companies = await readFinanceSalaryCompanies();
      setCompanyProfiles(companies);
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || "Failed to load finance companies."
      );
      setCompanyProfiles([]);
    }
  }, []);

  useEffect(() => {
    loadCompanyProfiles();
  }, [loadCompanyProfiles, companyProfilesVersion]);

  useEffect(() => {
    const onCompaniesUpdated = () => setCompanyProfilesVersion((v) => v + 1);
    window.addEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, onCompaniesUpdated);
    return () => window.removeEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, onCompaniesUpdated);
  }, []);
  const canCreate =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_MANAGEMENT, "create");
  const canUpdate =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_MANAGEMENT, "update");
  const canDelete =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_MANAGEMENT, "delete");
  const canReadDocuments =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_DOCUMENTS, "read");
  const canCreateDocuments =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_DOCUMENTS, "create");
  const canDeleteDocuments =
    hasAdminAccess(user) || checkPermission(permissionType.FINANCE_DOCUMENTS, "delete");

  const canReadFinanceTab = useCallback(
    (tab) => canReadFinanceManagementTab(user, tab),
    [user]
  );

  const expenseSection = useMemo(
    () => getExpenseSectionFromPath(location.pathname),
    [location.pathname]
  );

  const sectionExpensePermissionKey = (action) =>
    expenseSection === "office" ? `office_${action}` : action;

  const canReadSectionExpenses =
    hasAdminAccess(user) ||
    checkPermission(permissionType.FINANCE_EXPENSES, sectionExpensePermissionKey("read"));
  const canCreateExpenses =
    hasAdminAccess(user) ||
    checkPermission(permissionType.FINANCE_EXPENSES, sectionExpensePermissionKey("create"));
  const canUpdateExpenses =
    hasAdminAccess(user) ||
    checkPermission(permissionType.FINANCE_EXPENSES, sectionExpensePermissionKey("update"));
  const canDeleteExpenses =
    hasAdminAccess(user) ||
    checkPermission(permissionType.FINANCE_EXPENSES, sectionExpensePermissionKey("delete"));

  const visibleExpenseTabs = useMemo(
    () => EXPENSE_MANAGEMENT_TABS.filter((tab) => canReadExpenseManagementTab(user, tab)),
    [user]
  );

  const handleExpenseSectionChange = useCallback(
    (sectionKey) => {
      resetExpenseForm();
      navigate(getExpenseManagementTabPath(sectionKey));
    },
    [navigate]
  );

  const financePageSection = useMemo(
    () => getFinancePageSection(getFinanceTabKeyFromPath(location.pathname)),
    [location.pathname]
  );

  const visibleTabs = useMemo(
    () => getFinanceSectionTabs(financePageSection).filter((tab) => canReadFinanceTab(tab)),
    [financePageSection, canReadFinanceTab]
  );

  const visibleTabKeys = useMemo(
    () => visibleTabs.map((tab) => tab.key),
    [visibleTabs]
  );

  const pathTab = useMemo(
    () => getFinanceManagementTabFromPath(location.pathname, visibleTabKeys),
    [location.pathname, visibleTabKeys]
  );

  const defaultTabForSection = financePageSection === "finance" ? "dashboard" : "sheets";

  useEffect(() => {
    setActiveTab(pathTab);
  }, [pathTab]);

  useEffect(() => {
    const tab = [...SALARY_MANAGEMENT_TABS, ...FINANCE_OVERVIEW_TABS].find((item) => item.key === pathTab);
    if (tab && !canReadFinanceTab(tab)) {
      navigate(getFinanceManagementTabPath(visibleTabKeys[0] || defaultTabForSection), { replace: true });
    }
  }, [pathTab, canReadFinanceTab, visibleTabKeys, navigate, defaultTabForSection]);

  useEffect(() => {
    if (activeTab !== "expenses") return;
    if (!visibleExpenseTabs.length) {
      navigate(getFinanceManagementTabPath(visibleTabKeys[0] || defaultTabForSection), { replace: true });
      return;
    }
    if (!visibleExpenseTabs.some((tab) => tab.key === expenseSection)) {
      navigate(getExpenseManagementTabPath(visibleExpenseTabs[0].key), { replace: true });
    }
  }, [activeTab, expenseSection, visibleExpenseTabs, navigate, visibleTabKeys, defaultTabForSection]);

  const handleTabChange = useCallback((tabKey) => {
    navigate(getFinanceManagementTabPath(tabKey));
  }, [navigate]);

  const loadFinanceDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.FINANCE_DOCUMENTS,
        method: "GET",
        params: {
          year: filterYear,
          month: filterMonth,
        },
      });
      const documents = response?.data?.data?.documents;
      setFinanceDocuments(Array.isArray(documents) ? documents : []);
      setDocumentsTotalAmount(Number(response?.data?.data?.totalAmount) || 0);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to load finance documents.");
      setFinanceDocuments([]);
      setDocumentsTotalAmount(0);
    } finally {
      setDocumentsLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => {
    if (activeTab !== "expenses" || expenseSection !== "documents" || !canReadDocuments) return undefined;
    loadFinanceDocuments();
    return () => {
      setDocumentAttachmentViewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setViewingDocumentAttachment(null);
    };
  }, [activeTab, expenseSection, canReadDocuments, loadFinanceDocuments]);

  const resetDocumentForm = () => {
    setDocumentForm({ ...initialExpenseForm, expenseDate: getTodayInputValue() });
    setEditingDocumentId(null);
    setEditingDocumentMeta(null);
    setDocumentInvoiceFile(null);
    setDocumentUpiScreenshotFile(null);
    if (documentInvoiceInputRef.current) documentInvoiceInputRef.current.value = "";
    if (documentUpiScreenshotInputRef.current) documentUpiScreenshotInputRef.current.value = "";
    setShowDocumentForm(false);
  };

  const openCreateDocument = () => {
    setDocumentForm({ ...initialExpenseForm, expenseDate: getTodayInputValue() });
    setEditingDocumentId(null);
    setEditingDocumentMeta(null);
    setDocumentInvoiceFile(null);
    setDocumentUpiScreenshotFile(null);
    if (documentInvoiceInputRef.current) documentInvoiceInputRef.current.value = "";
    if (documentUpiScreenshotInputRef.current) documentUpiScreenshotInputRef.current.value = "";
    setShowDocumentForm(true);
  };

  const openEditDocument = (document) => {
    setDocumentForm({
      expenseType: document.expenseType || "",
      customExpenseType: document.customExpenseType || "",
      paymentMethod: document.paymentMethod || "",
      upiId: document.upiId || "",
      cardUsedBy: document.cardUsedBy || "",
      expenseDate: document.expenseDate
        ? String(document.expenseDate).slice(0, 10)
        : getTodayInputValue(),
      price: String(document.price ?? ""),
      description: document.description || document.reason || "",
      paidBy: document.paidBy || "",
    });
    setEditingDocumentId(document.id);
    setEditingDocumentMeta({
      hasInvoice: Boolean(document.hasInvoice),
      invoiceFileName: document.invoiceFileName || "",
      hasUpiScreenshot: Boolean(document.hasUpiScreenshot),
      upiScreenshotFileName: document.upiScreenshotFileName || "",
    });
    setDocumentInvoiceFile(null);
    setDocumentUpiScreenshotFile(null);
    if (documentInvoiceInputRef.current) documentInvoiceInputRef.current.value = "";
    if (documentUpiScreenshotInputRef.current) documentUpiScreenshotInputRef.current.value = "";
    setShowDocumentForm(true);
  };

  const saveDocument = async (event) => {
    event.preventDefault();
    if (!canCreateDocuments) return;
    if (editingDocumentId && !canCreateDocuments) return;

    if (!documentForm.expenseType) {
      showErrorToast("Select an expense type.");
      return;
    }
    if (documentForm.expenseType === EXPENSE_TYPE_OTHER && !documentForm.customExpenseType.trim()) {
      showErrorToast("Enter a custom expense type.");
      return;
    }
    if (!documentForm.paymentMethod) {
      showErrorToast("Select a payment method.");
      return;
    }
    if (documentForm.paymentMethod === "Card" && !documentForm.cardUsedBy.trim()) {
      showErrorToast("Enter whose card was used.");
      return;
    }
    if (!documentForm.expenseDate) {
      showErrorToast("Select the expense date.");
      return;
    }
    const price = toNumber(documentForm.price);
    if (price < 0) {
      showErrorToast("Enter a valid price.");
      return;
    }
    if (!documentForm.paidBy.trim()) {
      showErrorToast("Enter who paid for this expense.");
      return;
    }

    setDocumentSaving(true);
    try {
      const formData = new FormData();
      formData.append("expenseType", documentForm.expenseType);
      formData.append("customExpenseType", documentForm.customExpenseType.trim());
      formData.append("paymentMethod", documentForm.paymentMethod);
      formData.append("upiId", documentForm.upiId.trim());
      formData.append("cardUsedBy", documentForm.cardUsedBy.trim());
      formData.append("expenseDate", documentForm.expenseDate);
      formData.append("price", String(price));
      formData.append("description", documentForm.description.trim());
      formData.append("paidBy", documentForm.paidBy.trim());
      formData.append("expenseYear", String(filterYear));
      formData.append("expenseMonth", String(filterMonth));
      if (documentInvoiceFile) formData.append("invoice", documentInvoiceFile);
      if (documentUpiScreenshotFile) formData.append("upiScreenshot", documentUpiScreenshotFile);

      if (editingDocumentId) {
        await protectedApi({
          url: APIS.FINANCE_DOCUMENT.replace(":documentId", String(editingDocumentId)),
          method: "PUT",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          _dataEncrypted: true,
        });
        showSuccessToast("Document updated.");
      } else {
        await protectedApi({
          url: APIS.FINANCE_DOCUMENTS,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          _dataEncrypted: true,
        });
        showSuccessToast("Document saved.");
      }
      resetDocumentForm();
      await loadFinanceDocuments();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to save document.");
    } finally {
      setDocumentSaving(false);
    }
  };

  const handleViewDocumentAttachment = async (document, attachmentType) => {
    if (!document?.id) return;
    setViewingDocumentAttachment({ document, attachmentType });
    setDocumentAttachmentViewLoading(true);
    setDocumentAttachmentViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob =
        attachmentType === "invoice"
          ? await fetchFinanceDocumentInvoiceBlob(document.id)
          : await fetchFinanceDocumentUpiScreenshotBlob(document.id);
      const mimeType =
        attachmentType === "invoice"
          ? document.invoiceMimeType || blob.type
          : document.upiScreenshotMimeType || blob.type;
      if (isPdfMimeType(mimeType) || isImageMimeType(mimeType)) {
        const url = URL.createObjectURL(blob);
        setDocumentAttachmentViewUrl(url);
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        setViewingDocumentAttachment(null);
      }
    } catch (error) {
      showErrorToast(error?.message || "Unable to open attachment.");
      setViewingDocumentAttachment(null);
    } finally {
      setDocumentAttachmentViewLoading(false);
    }
  };

  const closeDocumentAttachmentViewer = () => {
    setDocumentAttachmentViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setViewingDocumentAttachment(null);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete?.id || !canDeleteDocuments) return;
    setDocumentDeleteLoading(true);
    try {
      await protectedApi({
        url: APIS.FINANCE_DOCUMENT_DELETE.replace(":documentId", String(documentToDelete.id)),
        method: "DELETE",
      });
      showSuccessToast("Document removed.");
      if (viewingDocumentAttachment?.document?.id === documentToDelete.id) {
        closeDocumentAttachmentViewer();
      }
      setDocumentToDelete(null);
      await loadFinanceDocuments();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to delete document.");
    } finally {
      setDocumentDeleteLoading(false);
    }
  };

  const formatDocumentDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const resetExpenseForm = () => {
    setExpenseForm({ ...initialExpenseForm, expenseDate: getTodayInputValue() });
    setEditingExpenseId(null);
    setEditingExpenseMeta(null);
    setExpenseInvoiceFile(null);
    setExpenseUpiScreenshotFile(null);
    if (expenseInvoiceInputRef.current) expenseInvoiceInputRef.current.value = "";
    if (expenseUpiScreenshotInputRef.current) expenseUpiScreenshotInputRef.current.value = "";
    setShowExpenseForm(false);
  };

  const openCreateExpense = () => {
    setExpenseForm({ ...initialExpenseForm, expenseDate: getTodayInputValue() });
    setEditingExpenseId(null);
    setEditingExpenseMeta(null);
    setExpenseInvoiceFile(null);
    setExpenseUpiScreenshotFile(null);
    if (expenseInvoiceInputRef.current) expenseInvoiceInputRef.current.value = "";
    if (expenseUpiScreenshotInputRef.current) expenseUpiScreenshotInputRef.current.value = "";
    setShowExpenseForm(true);
  };

  const openEditExpense = (expense) => {
    setExpenseForm({
      expenseType: expense.expenseType || "",
      customExpenseType: expense.customExpenseType || "",
      paymentMethod: expense.paymentMethod || "",
      upiId: expense.upiId || "",
      cardUsedBy: expense.cardUsedBy || "",
      expenseDate: expense.expenseDate
        ? String(expense.expenseDate).slice(0, 10)
        : getTodayInputValue(),
      price: String(expense.price ?? ""),
      description: expense.description || expense.reason || "",
      paidBy: expense.paidBy || "",
    });
    setEditingExpenseId(expense.id);
    setEditingExpenseMeta({
      hasInvoice: Boolean(expense.hasInvoice),
      invoiceFileName: expense.invoiceFileName || "",
      hasUpiScreenshot: Boolean(expense.hasUpiScreenshot),
      upiScreenshotFileName: expense.upiScreenshotFileName || "",
    });
    setExpenseInvoiceFile(null);
    setExpenseUpiScreenshotFile(null);
    if (expenseInvoiceInputRef.current) expenseInvoiceInputRef.current.value = "";
    if (expenseUpiScreenshotInputRef.current) expenseUpiScreenshotInputRef.current.value = "";
    setShowExpenseForm(true);
  };

  const handleViewExpenseAttachment = async (expense, attachmentType) => {
    if (!expense?.id) return;
    setViewingExpenseAttachment({ expense, attachmentType });
    setExpenseAttachmentViewLoading(true);
    setExpenseAttachmentViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const blob =
        attachmentType === "invoice"
          ? await fetchExpenseInvoiceBlob(expense.id)
          : await fetchExpenseUpiScreenshotBlob(expense.id);
      const mimeType =
        attachmentType === "invoice"
          ? expense.invoiceMimeType || blob.type
          : expense.upiScreenshotMimeType || blob.type;
      if (isPdfMimeType(mimeType) || isImageMimeType(mimeType)) {
        const url = URL.createObjectURL(blob);
        setExpenseAttachmentViewUrl(url);
      } else {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        setViewingExpenseAttachment(null);
      }
    } catch (error) {
      showErrorToast(error?.message || "Unable to open attachment.");
      setViewingExpenseAttachment(null);
    } finally {
      setExpenseAttachmentViewLoading(false);
    }
  };

  const closeExpenseAttachmentViewer = () => {
    setExpenseAttachmentViewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setViewingExpenseAttachment(null);
  };

  const formatExpenseDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderExpensePeriodFilters = () => (
    <div className="finance-filters">
      <div>
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Year</label>
        <Select
          className="min-w-[120px]"
          value={filterYear}
          onChange={handleYearChange}
          options={yearOptions}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Month</label>
        <Select
          className="min-w-[140px]"
          value={filterMonth}
          onChange={setFilterMonth}
          options={monthOptions}
        />
      </div>
    </div>
  );

  const yearOptions = useMemo(() => getFinanceYearOptions(), []);
  const monthOptions = useMemo(() => getFinanceMonthOptions(filterYear), [filterYear]);
  const periodStatus = useMemo(
    () => getFinancePeriodStatus(filterYear, filterMonth),
    [filterYear, filterMonth]
  );

  const handleYearChange = (year) => {
    setFilterYear(year);
    const allowedMonths = getFinanceMonthOptions(year);
    if (!allowedMonths.some((option) => option.value === filterMonth)) {
      const last = allowedMonths[allowedMonths.length - 1];
      if (last) setFilterMonth(last.value);
    }
  };

  const employeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : [])
        .filter((employee) => Number(employee?.roleId) !== 1 && !isCeoOrCmoRoleUser(employee))
        .map((employee) => ({
          value: String(employee.userId || employee.id),
          label: `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email,
          salaryCompanyId: String(employee.salaryCompanyId || "").trim(),
        }))
        .filter((option) => option.value && option.label),
    [employees]
  );

  const loadStructures = useCallback(async () => {
    const response = await protectedApi({
      url: `${APIS.FINANCE_SALARY_STRUCTURES}?includeRemoved=1`,
      method: "GET",
    });
    setStructures(response?.data?.data?.structures || []);
  }, []);

  const loadSheetImportEmployees = useCallback(async () => {
    try {
      const response = await protectedApi({
        url: APIS.FINANCE_EMPLOYEES_FOR_SHEET_IMPORT,
        method: "GET",
      });
      setSheetImportEmployees(response?.data?.data?.employees || []);
    } catch (error) {
      setSheetImportEmployees([]);
    }
  }, []);

  const defaultWorkingDaysForMonth = useMemo(
    () => getWorkingDaysInCalendarMonth(filterYear, filterMonth),
    [filterYear, filterMonth]
  );
  const effectiveWorkingDaysForMonth = activeSheet?.totalWorkingDays || defaultWorkingDaysForMonth;

  const financePeriodKey = `${filterYear}-${filterMonth}`;
  const isSheetLoading = loading || loadedPeriodKey !== financePeriodKey;

  const clearEntryFieldUiState = useCallback(() => {
    setPendingStatusByEntry({});
    setStatusSavingByEntry({});
    setTdsInputsByEntry({});
    setPtSavingByEntry({});
  }, []);

  const getEntryUiKey = useCallback(
    (entry) => `${financePeriodKey}-${entry?.id ?? ""}`,
    [financePeriodKey]
  );

  const fetchMonthlySheet = useCallback(async ({ sync = false } = {}) => {
    const syncQuery = sync ? "&sync=1" : "";
    const response = await protectedApi({
      url: `${APIS.FINANCE_SALARY_SHEET_BY_MONTH}?year=${filterYear}&month=${filterMonth}${syncQuery}`,
      method: "GET",
    });
    const {
      sheet = null,
      canGenerate,
      canCreateManual,
      isBeforeStart,
      isFuture,
    } = response?.data?.data || {};
    return {
      sheet,
      canGenerate: Boolean(canGenerate),
      canCreateManual: Boolean(canCreateManual),
      isBeforeStart: Boolean(isBeforeStart),
      isFuture: Boolean(isFuture),
    };
  }, [filterYear, filterMonth]);

  const applyMonthlySheetResult = useCallback((result) => {
    setActiveSheet(result.sheet);
    setPeriodMeta({
      canGenerate: result.canGenerate,
      canCreateManual: result.canCreateManual,
      isBeforeStart: Boolean(result.isBeforeStart),
      isFuture: Boolean(result.isFuture),
    });
    return result.sheet;
  }, []);

  const loadMonthlySheet = useCallback(async ({ sync = false } = {}) => {
    const result = await fetchMonthlySheet({ sync });
    applyMonthlySheetResult(result);
    return result;
  }, [applyMonthlySheetResult, fetchMonthlySheet]);

  const generateMonthlySheetIfNeeded = useCallback(async () => {
    let result = await fetchMonthlySheet();
    if (result.sheet) return result;
    if (result.canCreateManual) {
      await protectedApi({
        url: APIS.FINANCE_SALARY_SHEET_MANUAL,
        method: "POST",
        data: {
          year: filterYear,
          month: filterMonth,
          totalWorkingDays: getWorkingDaysInCalendarMonth(filterYear, filterMonth),
        },
      });
      result = await fetchMonthlySheet();
      return result;
    }
    if (!result.canGenerate) return result;
    await protectedApi({
      url: APIS.FINANCE_SALARY_SHEET_GENERATE,
      method: "POST",
      data: { year: filterYear, month: filterMonth },
    });
    result = await fetchMonthlySheet();
    return result;
  }, [fetchMonthlySheet, filterYear, filterMonth]);

  const applySheetToDashboard = useCallback(
    (sheet) => {
      setDashboard({
        year: filterYear,
        month: filterMonth,
        hasSheet: Boolean(sheet),
        workingDays: sheet?.totalWorkingDays || defaultWorkingDaysForMonth,
        summary: sheet?.summary || buildSheetSummary(sheet?.entries || []),
      });
    },
    [filterYear, filterMonth, defaultWorkingDaysForMonth]
  );

  const reloadSheetWithCalculations = useCallback(async (options) => {
    const result = await loadMonthlySheet(options);
    const sheet = result?.sheet ?? result;
    applySheetToDashboard(sheet);
    return sheet;
  }, [applySheetToDashboard, loadMonthlySheet]);

  const refreshFinanceData = useCallback(async () => {
    const requestId = periodLoadRequestRef.current + 1;
    periodLoadRequestRef.current = requestId;
    const periodKey = `${filterYear}-${filterMonth}`;
    setLoading(true);
    try {
      const result = await generateMonthlySheetIfNeeded();
      if (requestId !== periodLoadRequestRef.current) return;
      const sheet = applyMonthlySheetResult(result);
      setLoadedPeriodKey(periodKey);
      applySheetToDashboard(sheet);
      loadStructures().catch(() => { });
    } catch (error) {
      if (requestId !== periodLoadRequestRef.current) return;
      showErrorToast(error?.response?.data?.error?.message || "Failed to load finance data.");
      setActiveSheet(null);
      setLoadedPeriodKey(periodKey);
      applySheetToDashboard(null);
    } finally {
      if (requestId === periodLoadRequestRef.current) {
        setLoading(false);
      }
    }
  }, [
    applyMonthlySheetResult,
    applySheetToDashboard,
    filterMonth,
    filterYear,
    generateMonthlySheetIfNeeded,
    loadStructures,
  ]);

  useEffect(() => {
    dispatch(getEmployeesAPI({ page: 1, limit: 500, filterBy: "active" }));
    loadSheetImportEmployees();
  }, [dispatch, loadSheetImportEmployees]);

  useEffect(() => {
    clearEntryFieldUiState();
    setShowManualEntryForm(false);
    setManualSheetWorkingDays(String(getWorkingDaysInCalendarMonth(filterYear, filterMonth) || ""));
    refreshFinanceData();
  }, [filterYear, filterMonth, clearEntryFieldUiState, refreshFinanceData]);

  const isManualPeriod = periodStatus === "manual";
  const canDeleteSheetForPeriod = useMemo(
    () => canDeleteSalarySheetForPeriod(filterYear, filterMonth),
    [filterYear, filterMonth]
  );
  const canCreateManualSheet =
    canCreate &&
    (periodMeta.canCreateManual || canCreateManualFinanceSheet(filterYear, filterMonth));
  const canRefreshSheetCalculations = canCreate || canUpdate;

  const handleRefreshSheetCalculations = useCallback(async () => {
    if (
      !activeSheet
      || !canRefreshSheetCalculations
      || syncingSheet
      || loading
      || isManualPeriod
    ) {
      return;
    }
    setSyncingSheet(true);
    try {
      await reloadSheetWithCalculations({ sync: true });
      showSuccessToast("Salary sheet recalculated from latest leave and attendance.");
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to refresh salary sheet.");
    } finally {
      setSyncingSheet(false);
    }
  }, [
    activeSheet,
    canRefreshSheetCalculations,
    isManualPeriod,
    loading,
    reloadSheetWithCalculations,
    syncingSheet,
  ]);

  const showRefreshCalculationsButton = Boolean(
    activeSheet && canRefreshSheetCalculations && !isManualPeriod
  );

  useEffect(() => {
    const otherCompanies = financeCompaniesForTabs(companyProfiles);
    if (!companyProfiles.length && !otherCompanies.length) {
      setActiveSalaryCompanyId(getTheCodingStudioTabId([]));
      return;
    }
    const tabIds = [
      getTheCodingStudioTabId(companyProfiles),
      ...otherCompanies.map((company) => String(company.id)),
    ];
    setActiveSalaryCompanyId((current) => {
      if (current && tabIds.includes(current)) return current;
      if (current && isTheCodingStudioTabActive(current, companyProfiles)) {
        return getTheCodingStudioTabId(companyProfiles);
      }
      return tabIds[0];
    });
  }, [companyProfiles]);

  const salaryCompanyByUserId = useMemo(() => {
    const map = new Map();
    (Array.isArray(employees) ? employees : []).forEach((employee) => {
      const userId = Number(employee.userId || employee.id);
      if (!userId) return;
      const companyId = String(employee.salaryCompanyId || "").trim();
      if (companyId) map.set(userId, companyId);
    });
    return map;
  }, [employees]);

  const lastWorkingDateByUserId = useMemo(() => {
    const map = new Map();
    const add = (userId, value) => {
      const id = Number(userId);
      if (!id || !value) return;
      map.set(id, value);
    };
    (Array.isArray(employees) ? employees : []).forEach((employee) => {
      add(employee.userId || employee.id, employee.lastWorkingDate);
    });
    (Array.isArray(sheetImportEmployees) ? sheetImportEmployees : []).forEach((employee) => {
      add(employee.userId || employee.id, employee.lastWorkingDate);
    });
    (Array.isArray(structures) ? structures : []).forEach((structure) => {
      add(structure.userId, structure.lastWorkingDate);
    });
    return map;
  }, [employees, sheetImportEmployees, structures]);

  const searchedEntries = useMemo(() => {
    if (isSheetLoading) return [];
    const entries = activeSheet?.entries || [];
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter((row) =>
      String(row.employeeName || "")
        .toLowerCase()
        .includes(query)
    );
  }, [activeSheet, employeeSearch, isSheetLoading]);

  const otherCompanyProfiles = useMemo(
    () => financeCompaniesForTabs(companyProfiles),
    [companyProfiles]
  );
  const theCodingStudioTabId = useMemo(
    () => getTheCodingStudioTabId(companyProfiles),
    [companyProfiles]
  );

  const activeTabSalaryCompanyId = useMemo(
    () => resolveSalaryCompanyIdForActiveTab(activeSalaryCompanyId, companyProfiles),
    [activeSalaryCompanyId, companyProfiles]
  );

  const loadSalaryExpenses = useCallback(async () => {
    setExpensesLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.FINANCE_EXPENSES,
        method: "GET",
        params: {
          year: filterYear,
          month: filterMonth,
        },
      });
      const expenses = response?.data?.data?.expenses;
      setSalaryExpenses(Array.isArray(expenses) ? expenses : []);
      setExpensesTotalAmount(Number(response?.data?.data?.totalAmount) || 0);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to load expenses.");
      setSalaryExpenses([]);
      setExpensesTotalAmount(0);
    } finally {
      setExpensesLoading(false);
    }
  }, [filterYear, filterMonth]);

  useEffect(() => {
    if (
      activeTab !== "expenses" ||
      (expenseSection !== "list" && expenseSection !== "office") ||
      !canReadSectionExpenses
    ) {
      return undefined;
    }
    loadSalaryExpenses();
    return undefined;
  }, [activeTab, expenseSection, canReadSectionExpenses, loadSalaryExpenses]);

  const filteredSalaryExpenses = useMemo(
    () => filterExpensesBySection(salaryExpenses, expenseSection),
    [salaryExpenses, expenseSection]
  );

  const filteredExpensesTotalAmount = useMemo(
    () => filteredSalaryExpenses.reduce((sum, item) => sum + (Number(item.price) || 0), 0),
    [filteredSalaryExpenses]
  );

  const expenseTypeOptionsForSection = useMemo(
    () => getExpenseTypeOptionsForSection(expenseSection),
    [expenseSection]
  );

  const isExpenseListSection = expenseSection === "list" || expenseSection === "office";

  const saveExpense = async (event) => {
    event.preventDefault();
    if (!canCreateExpenses && !editingExpenseId) return;
    if (!canUpdateExpenses && editingExpenseId) return;

    if (!expenseForm.expenseType) {
      showErrorToast("Select an expense type.");
      return;
    }
    if (expenseSection === "office" && !isOfficeExpenseType(expenseForm.expenseType)) {
      showErrorToast("Select an office expense type.");
      return;
    }
    if (expenseSection === "list" && isOfficeExpenseType(expenseForm.expenseType)) {
      showErrorToast("Office expenses must be added from the Office Expenses tab.");
      return;
    }
    if (expenseForm.expenseType === EXPENSE_TYPE_OTHER && !expenseForm.customExpenseType.trim()) {
      showErrorToast("Enter a custom expense type.");
      return;
    }
    if (!expenseForm.paymentMethod) {
      showErrorToast("Select a payment method.");
      return;
    }
    if (expenseForm.paymentMethod === "Card" && !expenseForm.cardUsedBy.trim()) {
      showErrorToast("Enter whose card was used.");
      return;
    }
    if (!expenseForm.expenseDate) {
      showErrorToast("Select the expense date.");
      return;
    }
    const expensePeriod = getExpensePeriodFromDate(expenseForm.expenseDate);
    if (!expensePeriod) {
      showErrorToast("Select a valid expense date.");
      return;
    }
    const price = toNumber(expenseForm.price);
    if (price < 0) {
      showErrorToast("Enter a valid price.");
      return;
    }
    if (!expenseForm.paidBy.trim()) {
      showErrorToast("Enter who paid for this expense.");
      return;
    }

    setExpenseSaving(true);
    try {
      const formData = new FormData();
      formData.append("expenseType", expenseForm.expenseType);
      formData.append("customExpenseType", expenseForm.customExpenseType.trim());
      formData.append("paymentMethod", expenseForm.paymentMethod);
      formData.append("upiId", expenseForm.upiId.trim());
      formData.append("cardUsedBy", expenseForm.cardUsedBy.trim());
      formData.append("expenseDate", expenseForm.expenseDate);
      formData.append("price", String(price));
      formData.append("description", expenseForm.description.trim());
      formData.append("paidBy", expenseForm.paidBy.trim());
      // Bucket by expense date so the entry appears under that month in the period filter.
      formData.append("expenseYear", String(expensePeriod.year));
      formData.append("expenseMonth", String(expensePeriod.month));
      if (expenseInvoiceFile) formData.append("invoice", expenseInvoiceFile);
      if (expenseUpiScreenshotFile) formData.append("upiScreenshot", expenseUpiScreenshotFile);

      if (editingExpenseId) {
        await protectedApi({
          url: APIS.FINANCE_EXPENSE.replace(":expenseId", String(editingExpenseId)),
          method: "PUT",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          _dataEncrypted: true,
        });
        showSuccessToast("Expense updated.");
      } else {
        await protectedApi({
          url: APIS.FINANCE_EXPENSES,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
          _dataEncrypted: true,
        });
        showSuccessToast("Expense added.");
      }
      resetExpenseForm();
      const periodChanged =
        expensePeriod.year !== filterYear || expensePeriod.month !== filterMonth;
      if (periodChanged) {
        setFilterYear(expensePeriod.year);
        setFilterMonth(expensePeriod.month);
      } else {
        await loadSalaryExpenses();
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to save expense.");
    } finally {
      setExpenseSaving(false);
    }
  };

  const confirmDeleteExpense = async () => {
    if (!expenseToDelete?.id || !canDeleteExpenses) return;
    setExpenseDeleteLoading(true);
    try {
      await protectedApi({
        url: APIS.FINANCE_EXPENSE.replace(":expenseId", String(expenseToDelete.id)),
        method: "DELETE",
      });
      showSuccessToast("Expense removed.");
      setExpenseToDelete(null);
      await loadSalaryExpenses();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to delete expense.");
    } finally {
      setExpenseDeleteLoading(false);
    }
  };

  const filteredEntries = useMemo(() => {
    if (!String(activeSalaryCompanyId || "").trim()) return [];
    return searchedEntries.filter((entry) =>
      matchesActiveSalaryCompanyTab(
        entry,
        activeSalaryCompanyId,
        companyProfiles,
        salaryCompanyByUserId
      )
    );
  }, [searchedEntries, activeSalaryCompanyId, companyProfiles, salaryCompanyByUserId]);

  const filteredStructures = useMemo(() => {
    if (!String(activeSalaryCompanyId || "").trim()) return [];
    return structures
      .filter((structure) => !isCeoOrCmoRoleUser({ roleId: structure.roleId }))
      .filter((structure) =>
        matchesActiveSalaryCompanyTab(
          {
            userId: structure.userId,
            salaryCompanyId: structure.salaryCompanyId,
          },
          activeSalaryCompanyId,
          companyProfiles,
          salaryCompanyByUserId
        )
      );
  }, [structures, activeSalaryCompanyId, companyProfiles, salaryCompanyByUserId]);

  const employeeOptionsForActiveCompany = useMemo(() => {
    if (!String(activeSalaryCompanyId || "").trim()) return [];
    return employeeOptions
      .filter((option) =>
        matchesActiveSalaryCompanyTab(
          { userId: option.value, salaryCompanyId: option.salaryCompanyId },
          activeSalaryCompanyId,
          companyProfiles,
          salaryCompanyByUserId
        )
      )
      .map(({ value, label }) => ({ value, label }));
  }, [employeeOptions, activeSalaryCompanyId, companyProfiles, salaryCompanyByUserId]);

  const sheetSummary = useMemo(() => buildSheetSummary(filteredEntries), [filteredEntries]);

  const activeCompanyLabel = isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles)
    ? DEFAULT_SALARY_COMPANY_NAME
    : getFinanceSalaryCompanyLabelSync(activeSalaryCompanyId, companyProfiles);

  const activeFilteredStructures = useMemo(
    () => filteredStructures.filter((structure) => !isRemovedStructure(structure)),
    [filteredStructures]
  );

  const structurePayrollTotal = useMemo(
    () =>
      activeFilteredStructures.reduce((sum, row) => sum + toNumber(row.monthlySalary), 0),
    [activeFilteredStructures]
  );

  const removedStructuresCount = filteredStructures.length - activeFilteredStructures.length;

  const openCreateStructure = () => {
    setStructureForm(initialStructureForm);
    setEditingStructureId(null);
    setShowStructureForm(true);
  };

  const openEditStructure = (structure) => {
    setStructureForm({
      userId: String(structure.userId),
      designation: structure.designation || "",
      monthlySalary: structure.monthlySalary ?? "",
    });
    setEditingStructureId(structure.id);
    setShowStructureForm(true);
  };

  const saveStructure = async (event) => {
    event.preventDefault();
    if (!canCreate && !canUpdate) {
      showErrorToast("You do not have permission to save salary structures.");
      return;
    }
    if (!structureForm.userId) {
      showErrorToast("Please select an employee.");
      return;
    }
    const monthlySalary = toNumber(structureForm.monthlySalary);
    if (monthlySalary < 0) {
      showErrorToast("Salary cannot be negative.");
      return;
    }
    const salaryBreakdown = getSalaryStructureBreakdown(structureForm.designation, monthlySalary);
    try {
      await protectedApi({
        url: APIS.FINANCE_SALARY_STRUCTURES,
        method: "POST",
        data: {
          userId: structureForm.userId,
          designation: structureForm.designation,
          ...salaryBreakdown,
        },
      });
      showSuccessToast(editingStructureId ? "Salary structure updated." : "Salary structure saved.");
      setShowStructureForm(false);
      await loadStructures();
      await reloadSheetWithCalculations({ sync: true });
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to save salary structure.");
    }
  };

  const confirmDeleteStructure = async ({ lastWorkingDate, lastWorkingYear, lastWorkingMonth } = {}) => {
    if (!structureToDelete?.id || structureDeleteLoading) return;
    setStructureDeleteLoading(true);
    try {
      const url = APIS.FINANCE_SALARY_STRUCTURE_DELETE.replace(":structureId", structureToDelete.id);
      await protectedApi({
        url,
        method: "DELETE",
        data: {
          lastWorkingDate: lastWorkingDate || undefined,
          lastWorkingYear,
          lastWorkingMonth,
        },
      });
      showSuccessToast("Employee removed from salary structure and later salary sheets.");
      setStructureToDelete(null);
      await loadStructures();
      await reloadSheetWithCalculations({ sync: true });
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to remove employee from salary structure.");
    } finally {
      setStructureDeleteLoading(false);
    }
  };

  const createManualSalarySheet = async () => {
    if (!canCreateManualSheet || manualSheetCreating) return;
    setManualSheetCreating(true);
    try {
      const workingDaysInput = manualSheetWorkingDays.trim();
      const response = await protectedApi({
        url: APIS.FINANCE_SALARY_SHEET_MANUAL,
        method: "POST",
        data: {
          year: filterYear,
          month: filterMonth,
          ...(workingDaysInput !== "" ? { totalWorkingDays: toNumber(workingDaysInput) } : {}),
        },
      });
      const createdSheet = response?.data?.data?.sheet ?? null;
      if (createdSheet) {
        setActiveSheet(createdSheet);
        applySheetToDashboard(createdSheet);
      } else {
        await reloadSheetWithCalculations();
      }
      const entryCount = createdSheet?.entries?.length ?? 0;
      showSuccessToast(
        entryCount
          ? `Salary sheet loaded with ${entryCount} existing employee row(s) across all companies.`
          : "Manual salary sheet is ready. Import or add employees per company tab."
      );
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to create manual salary sheet.");
    } finally {
      setManualSheetCreating(false);
    }
  };

  const resetManualEntryForm = () => {
    setManualEntryMode("employee");
    setManualEntryForm({
      userId: "",
      manualUserId: "",
      employeeName: "",
      monthlySalary: "",
      basicSalary: "",
      accommodation: "",
      designation: "",
      leaveDays: "0",
      workedDays: "",
      ytdPaidLeavesBeforeMonth: "0",
      tds: "",
      professionalTaxEnabled: true,
    });
  };

  const openManualEntryForm = () => {
    resetManualEntryForm();
    setShowManualEntryForm(true);
  };

  const onManualEntryEmployeeChange = (userId) => {
    const structure = structures.find((row) => String(row.userId) === String(userId));
    const employee =
      sheetImportEmployees.find((row) => String(row.id) === String(userId))
      || (Array.isArray(employees) ? employees : []).find(
        (row) => String(row.userId || row.id) === String(userId)
      );
    const designation = structure?.designation || employee?.designation || "";
    if (isManualPeriod) {
      setManualEntryForm((prev) => ({
        ...prev,
        userId,
        designation,
        monthlySalary: "",
        basicSalary: "",
        accommodation: "",
      }));
      return;
    }
    const monthlySalary = structure ? calculateGrossFromStructure(structure) : "";
    const breakdown = monthlySalary !== "" ? getSalaryStructureBreakdown(designation, monthlySalary) : null;
    setManualEntryForm((prev) => ({
      ...prev,
      userId,
      designation,
      monthlySalary: monthlySalary !== "" ? String(monthlySalary) : "",
      basicSalary: breakdown ? String(breakdown.basicSalary) : "",
      accommodation: breakdown ? String(breakdown.hra) : "",
    }));
  };

  const saveManualEntry = async (event) => {
    event.preventDefault();
    if (!canCreate || !activeSheet?.id || manualEntrySaving) return;
    if (manualEntryMode === "employee" && !manualEntryForm.userId) {
      showErrorToast("Please select an employee.");
      return;
    }
    if (manualEntryMode === "former" && !manualEntryForm.employeeName.trim()) {
      showErrorToast("Please enter the previous employee name.");
      return;
    }
    if (
      manualEntryMode === "former"
      && manualEntryForm.manualUserId.trim()
      && !Number.isFinite(Number(manualEntryForm.manualUserId))
    ) {
      showErrorToast("User Id must be a valid number.");
      return;
    }
    const monthlySalary = toNumber(manualEntryForm.monthlySalary);
    if (monthlySalary < 0) {
      showErrorToast("Salary cannot be negative.");
      return;
    }
    if (isManualPeriod && monthlySalary <= 0) {
      showErrorToast("Enter the salary that applied for this month (not the employee's current salary).");
      return;
    }

    const sheetEntries = activeSheet?.entries || [];
    const targetUserId =
      manualEntryMode === "employee"
        ? Number(manualEntryForm.userId)
        : manualEntryForm.manualUserId.trim()
          ? Number(manualEntryForm.manualUserId)
          : null;
    if (targetUserId) {
      const existingByUser = sheetEntries.find((entry) => Number(entry.userId) === targetUserId);
      if (existingByUser) {
        showErrorToast(
          `${existingByUser.employeeName || "This employee"} is already on this salary sheet.`
        );
        return;
      }
    }
    const nameToCheck =
      manualEntryMode === "former"
        ? manualEntryForm.employeeName.trim().toLowerCase()
        : "";
    if (nameToCheck) {
      const existingByName = sheetEntries.find(
        (entry) => String(entry.employeeName || "").trim().toLowerCase() === nameToCheck
      );
      if (existingByName) {
        showErrorToast(
          `"${existingByName.employeeName}" is already on this salary sheet.`
        );
        return;
      }
    }

    setManualEntrySaving(true);
    try {
      const salaryBreakdown = getSalaryStructureBreakdown(
        manualEntryForm.designation,
        monthlySalary
      );
      const basicSalary = manualEntryForm.basicSalary.trim() !== ""
        ? toNumber(manualEntryForm.basicSalary)
        : salaryBreakdown.basicSalary;
      const accommodation = manualEntryForm.accommodation.trim() !== ""
        ? toNumber(manualEntryForm.accommodation)
        : salaryBreakdown.hra;
      const url = APIS.FINANCE_SALARY_SHEET_ENTRIES.replace(":sheetId", String(activeSheet.id));
      await protectedApi({
        url,
        method: "POST",
        data: {
          ...(manualEntryMode === "employee" ? { userId: manualEntryForm.userId } : {}),
          ...(manualEntryMode === "former"
            ? {
              employeeName: manualEntryForm.employeeName.trim(),
              ...(manualEntryForm.manualUserId.trim()
                ? { userId: Number(manualEntryForm.manualUserId) }
                : {}),
            }
            : {}),
          ...(activeTabSalaryCompanyId ? { salaryCompanyId: activeTabSalaryCompanyId } : {}),
          designation: manualEntryForm.designation,
          monthlySalary,
          basicSalary,
          accommodation,
          leaveDays: toNumber(manualEntryForm.leaveDays),
          ...(manualEntryForm.leaveDeduction !== ""
            ? { leaveDeduction: toNumber(manualEntryForm.leaveDeduction) }
            : {}),
          ytdPaidLeavesBeforeMonth: 0,
          ...(manualEntryForm.tds !== "" ? { tds: toNumber(manualEntryForm.tds) } : {}),
          professionalTaxEnabled: manualEntryForm.professionalTaxEnabled,
        },
      });
      showSuccessToast("Employee added to salary sheet.");
      setShowManualEntryForm(false);
      resetManualEntryForm();
      await reloadSheetWithCalculations();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to add employee.");
    } finally {
      setManualEntrySaving(false);
    }
  };

  const handleBulkImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeSheet?.id || bulkImporting) return;
    setBulkImporting(true);
    try {
      const { entries, parseErrors } = await parseManualSheetImportFile(file);
      const url = APIS.FINANCE_SALARY_SHEET_ENTRIES_BULK.replace(":sheetId", String(activeSheet.id));
      const response = await protectedApi({
        url,
        method: "POST",
        data: {
          entries,
          ...(activeTabSalaryCompanyId ? { salaryCompanyId: activeTabSalaryCompanyId } : {}),
        },
      });
      const result = response?.data?.data || {};
      const { createdCount = 0, failedCount = 0, failed = [] } = result;
      if (createdCount > 0) {
        showSuccessToast(
          failedCount
            ? `Imported ${createdCount} row(s). ${failedCount} failed.`
            : `Imported ${createdCount} employee row(s).`
        );
        await reloadSheetWithCalculations();
      } else {
        showErrorToast("No rows were imported. Check the file and try again.");
      }
      if (failed.length) {
        const preview = failed
          .slice(0, 3)
          .map((row) => `Row ${row.rowNumber}: ${row.message}`)
          .join(" ");
        showErrorToast(preview + (failed.length > 3 ? " …" : ""));
      }
      if (parseErrors.length) {
        showErrorToast(
          `${parseErrors.length} row(s) skipped while reading the file (invalid or empty lines).`
        );
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || error.message || "Bulk import failed.");
    } finally {
      event.target.value = "";
      setBulkImporting(false);
    }
  };

  const confirmDeleteManualEntry = async () => {
    if (!entryToDelete?.id || entryDeleteLoading) return;
    if (isSalaryEntryPaid(entryToDelete.status)) {
      showErrorToast("Paid salary entries cannot be removed.");
      setEntryToDelete(null);
      return;
    }
    setEntryDeleteLoading(true);
    try {
      const url = APIS.FINANCE_SALARY_ENTRY_DELETE.replace(":entryId", String(entryToDelete.id));
      await protectedApi({ url, method: "DELETE" });
      showSuccessToast("Employee removed from salary sheet.");
      setEntryToDelete(null);
      await reloadSheetWithCalculations();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to remove employee.");
    } finally {
      setEntryDeleteLoading(false);
    }
  };

  const confirmDeleteSheet = async () => {
    if (!sheetToDelete?.id || sheetDeleteLoading || !canDeleteSheetForPeriod) return;
    setSheetDeleteLoading(true);
    try {
      const url = APIS.FINANCE_SALARY_SHEET_DELETE.replace(":sheetId", sheetToDelete.id);
      await protectedApi({ url, method: "DELETE" });
      showSuccessToast("Entire month salary sheet deleted.");
      setSheetToDelete(null);
      setActiveSheet(null);
      await refreshFinanceData();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to delete salary sheet.");
    } finally {
      setSheetDeleteLoading(false);
    }
  };

  const confirmClearCompanyData = async () => {
    if (
      !companyDataToClear?.sheetId ||
      !companyDataToClear?.salaryCompanyId ||
      companyDataClearLoading ||
      !canDeleteSheetForPeriod
    ) {
      return;
    }
    setCompanyDataClearLoading(true);
    try {
      const url = APIS.FINANCE_SALARY_SHEET_ENTRIES_BY_COMPANY.replace(
        ":sheetId",
        String(companyDataToClear.sheetId)
      );
      const response = await protectedApi({
        url,
        method: "DELETE",
        data: { salaryCompanyId: companyDataToClear.salaryCompanyId },
      });
      const deletedCount = response?.data?.data?.deletedCount ?? companyDataToClear.entryCount ?? 0;
      showSuccessToast(
        `Removed ${deletedCount} employee row(s) for ${companyDataToClear.companyLabel || "this company"}. Other companies on this month are unchanged.`
      );
      setCompanyDataToClear(null);
      await reloadSheetWithCalculations();
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to clear company salary data.");
    } finally {
      setCompanyDataClearLoading(false);
    }
  };

  const buildEntryRecalcPayload = (entry) => ({
    leaveDays: entry.leaveDays,
    workedDays: entry.workedDays,
    monthlySalary: entry.monthlySalary,
    basicSalary: entry.basicSalary,
    accommodation: entry.accommodation,
    employeeName: entry.employeeName,
    designation: entry.designation,
    professionalTaxEnabled: isEntryProfessionalTaxEnabled(entry),
    ytdPaidLeavesBeforeMonth: isManualPeriod ? 0 : entry.ytdPaidLeavesBeforeMonth,
    tdsMode: "auto",
  });

  const getSheetLeaveInputValue = (row) =>
    isManualPeriod ? row.leaveDays : row.unpaidLeaveDays ?? row.leaveDays;

  const applyUpdatedSheetEntry = useCallback(
    (updatedEntry) => {
      if (!updatedEntry?.id) return;
      let nextSheetForDashboard = null;
      setActiveSheet((prev) => {
        if (!prev?.entries) return prev;
        const nextEntries = prev.entries.map((row) =>
          Number(row.id) === Number(updatedEntry.id) ? { ...row, ...updatedEntry } : row
        );
        nextSheetForDashboard = {
          ...prev,
          entries: nextEntries,
          summary: buildSheetSummary(nextEntries),
        };
        return nextSheetForDashboard;
      });
      if (nextSheetForDashboard) {
        applySheetToDashboard(nextSheetForDashboard);
      }
    },
    [applySheetToDashboard]
  );

  const toggleEntryProfessionalTax = async (entry, enabled) => {
    if (!canUpdate || isTraineeDesignation(entry.designation)) return;
    const entryUiKey = getEntryUiKey(entry);
    if (ptSavingByEntry[entryUiKey]) return;
    setPtSavingByEntry((prev) => ({ ...prev, [entryUiKey]: true }));
    try {
      const url = APIS.FINANCE_SALARY_ENTRY_UPDATE.replace(":entryId", entry.id);
      const response = await protectedApi({
        url,
        method: "PUT",
        data: {
          ...buildEntryRecalcPayload(entry),
          updateProfessionalTax: true,
          professionalTaxEnabled: enabled,
          professionalTax: enabled ? PROFESSIONAL_TAX_MONTHLY : 0,
          ...(isManualPeriod && entry.leaveDeduction != null
            ? { leaveDeduction: entry.leaveDeduction }
            : {}),
        },
      });
      const updatedEntry = response?.data?.data?.entry;
      if (updatedEntry) {
        applyUpdatedSheetEntry(updatedEntry);
      }
      await reloadSheetWithCalculations();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || "Failed to update professional tax setting."
      );
    } finally {
      setPtSavingByEntry((prev) => {
        const next = { ...prev };
        delete next[entryUiKey];
        return next;
      });
    }
  };

  const updateEntryField = async (entry, field, rawValue) => {
    if (!canUpdate) return;
    if (
      activeSheet?.id &&
      entry.sheetId != null &&
      Number(entry.sheetId) !== Number(activeSheet.id)
    ) {
      showErrorToast("This row belongs to another month. Wait for the sheet to finish loading.");
      return;
    }
    if (field === "status" && isSalaryEntryPaid(entry.status)) {
      showErrorToast("Paid salary status cannot be changed.");
      return;
    }

    const entryUiKey = getEntryUiKey(entry);
    if (field === "status" && statusSavingByEntry[entryUiKey]) return;

    const isStatusUpdate = field === "status";
    if (isStatusUpdate) {
      setStatusSavingByEntry((prev) => ({ ...prev, [entryUiKey]: true }));
      setPendingStatusByEntry((prev) => ({ ...prev, [entryUiKey]: rawValue }));
    }

    try {
      const url = APIS.FINANCE_SALARY_ENTRY_UPDATE.replace(":entryId", entry.id);
      const tdsInput = tdsInputsByEntry[entryUiKey];
      const tdsPayload = tdsInput !== "" && tdsInput !== undefined
        ? { tdsMode: "monthly", tds: toNumber(tdsInput) }
        : { tdsMode: "auto" };

      let data;
      if (isStatusUpdate) {
        data = { status: rawValue, updateStatus: true };
      } else if (isManualPeriod && field === "leaveDeduction") {
        data = {
          ...buildEntryRecalcPayload(entry),
          leaveDeduction: toNumber(rawValue),
          ...tdsPayload,
        };
      } else if (field === "leaveDays" || field === "workedDays") {
        const workingDays = activeSheet?.totalWorkingDays || defaultWorkingDaysForMonth;
        const synced = syncLeaveAndWorkedDaysForSheetEntry({
          workingDays,
          field,
          rawValue,
          entry,
          isManualPeriod,
        });
        data = {
          ...buildEntryRecalcPayload(entry),
          leaveDays: synced.leaveDays,
          workedDays: synced.workedDays,
          ...tdsPayload,
        };
      } else {
        data = {
          ...buildEntryRecalcPayload(entry),
          ...tdsPayload,
        };
      }

      const response = await protectedApi({
        url,
        method: "PUT",
        data,
      });
      const updatedEntry = response?.data?.data?.entry;
      if (updatedEntry) {
        applyUpdatedSheetEntry(updatedEntry);
      } else {
        await reloadSheetWithCalculations();
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to update salary entry.");
    } finally {
      if (isStatusUpdate) {
        setStatusSavingByEntry((prev) => ({ ...prev, [entryUiKey]: false }));
        setPendingStatusByEntry((prev) => {
          const nextPending = { ...prev };
          delete nextPending[entryUiKey];
          return nextPending;
        });
      }
    }
  };

  const updateEntryTds = async (entry, overrideInput) => {
    if (!canUpdate) return;
    const entryUiKey = getEntryUiKey(entry);
    setTdsSavingByEntry((prev) => ({ ...prev, [entryUiKey]: true }));
    try {
      const tdsInput = overrideInput !== undefined ? overrideInput : tdsInputsByEntry[entryUiKey];
      const data =
        tdsInput !== "" && tdsInput !== undefined
          ? { ...buildEntryRecalcPayload(entry), tdsMode: "monthly", tds: toNumber(tdsInput) }
          : { ...buildEntryRecalcPayload(entry), tdsMode: "auto" };
      const url = APIS.FINANCE_SALARY_ENTRY_UPDATE.replace(":entryId", entry.id);
      await protectedApi({ url, method: "PUT", data });
      await reloadSheetWithCalculations();
      setTdsInputsByEntry((prev) => ({ ...prev, [entryUiKey]: "" }));
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to update TDS.");
    } finally {
      setTdsSavingByEntry((prev) => ({ ...prev, [entryUiKey]: false }));
    }
  };

  const exportRows = () =>
    filteredEntries.map((row, index) => {
      const base = {
        "Sr No": index + 1,
        "Employees Name": row.employeeName,
        PT: isEntryProfessionalTaxEnabled(row) ? "On" : "Off",
        "Salary Per Month": row.monthlySalary,
        "Minimum Rate of Salary / Month (Basic)": getSheetDisplayBasicSalary(row),
        Designation: row.designation,
        "Total Working Days": row.totalWorkingDays,
        Leaves: isManualPeriod ? row.leaveDays : row.unpaidLeaveDays ?? row.leaveDays,
        "Leaves Deduction": row.leaveDeduction,
        Basic: getSheetDisplayBasicSalary(row),
        Accommodation: row.accommodation,
        "Total Gross Earnings": row.grossEarnings,
        "Professional Tax": row.professionalTax,
        TDS: row.tds,
        "Total Deduction": row.totalDeduction,
        "Net Salary": row.netSalary,
        Status: getSalaryEntryStatusLabel(row.status),
      };
      if (!isManualPeriod) {
        base["Total Worked Days"] = row.workedDays;
      }
      return base;
    });

  const exportExcel = () => {
    if (!filteredEntries.length) {
      showErrorToast("No salary sheet rows to export.");
      return;
    }
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows());
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      `${getMonthLabel(filterMonth)}-${filterYear}`
    );
    XLSX.writeFile(workbook, `salary-sheet-${filterYear}-${filterMonth}.xlsx`);
  };

  const exportPdf = () => {
    if (!filteredEntries.length) {
      showErrorToast("No salary sheet rows to export.");
      return;
    }
    const pdf = new jsPDF("landscape", "pt", "a4");
    const title = `Monthly Salary Sheet — ${getMonthLabel(filterMonth)} ${filterYear}`;
    pdf.setFontSize(14);
    pdf.text(title, 40, 40);
    pdf.setFontSize(9);
    let y = 70;
    const header = SHEET_COLUMNS.join(" | ");
    pdf.text(header, 40, y, { maxWidth: 760 });
    y += 18;
    filteredEntries.forEach((row, index) => {
      const line = [
        index + 1,
        row.employeeName,
        isEntryProfessionalTaxEnabled(row) ? "PT On" : "PT Off",
        row.monthlySalary,
        getSheetDisplayBasicSalary(row),
        row.designation,
        row.totalWorkingDays,
        row.unpaidLeaveDays ?? row.leaveDays,
        row.workedDays,
        row.leaveDeduction,
        getSheetDisplayBasicSalary(row),
        row.accommodation,
        row.grossEarnings,
        row.professionalTax,
        row.tds,
        row.totalDeduction,
        row.netSalary,
        getSalaryEntryStatusLabel(row.status),
      ].join(" | ");
      if (y > 520) {
        pdf.addPage();
        y = 40;
      }
      pdf.text(line, 40, y, { maxWidth: 760 });
      y += 14;
    });
    pdf.save(`salary-sheet-${filterYear}-${filterMonth}.pdf`);
  };

  const handleCompanyCreated = (company) => {
    if (company?.id != null) {
      setActiveSalaryCompanyId(String(company.id));
    }
  };

  const renderSalaryCompanyTabs = ({ showAddCompany = true } = {}) => (
    <div className="finance-company-toolbar">
      <nav className="finance-company-tabs" aria-label="Salary company">
        <button
          type="button"
          className={`finance-tab ${isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles) ? "active" : ""
            }`}
          onClick={() => setActiveSalaryCompanyId(theCodingStudioTabId)}
        >
          {DEFAULT_SALARY_COMPANY_NAME}
        </button>
        {otherCompanyProfiles.map((company) => {
          const companyId = String(company.id);
          return (
            <button
              key={companyId}
              type="button"
              className={`finance-tab ${activeSalaryCompanyId === companyId ? "active" : ""}`}
              onClick={() => setActiveSalaryCompanyId(companyId)}
            >
              {company.companyName?.trim() || `Company ${companyId}`}
            </button>
          );
        })}
      </nav>
      {showAddCompany && !otherCompanyProfiles.length ? (
        <p className="finance-company-toolbar__hint text-sm text-[#6f7d97] w-full mt-1">
          Employees without a salary company appear under {DEFAULT_SALARY_COMPANY_NAME}. Use{" "}
          <strong>Add company</strong> for other employers.
        </p>
      ) : null}
      {showAddCompany ? (
        <button
          type="button"
          className="finance-add-company-btn"
          onClick={() => setCompanyFormOpen(true)}
        >
          + Add company
        </button>
      ) : null}
    </div>
  );

  const renderPeriodNotice = () => {
    if (periodStatus === "too_early") {
      return (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Manual salary sheets are available from {getMonthLabel(MANUAL_SHEET_MIN.month)} {MANUAL_SHEET_MIN.year}{" "}
          onward.
        </p>
      );
    }
    if (periodStatus === "manual") {
      return (
        <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
          Historical month (before {getMonthLabel(FINANCE_SHEET_START.month)} {FINANCE_SHEET_START.year}). Download the
          Excel template per company tab, edit amounts, and upload on that tab. All companies share one sheet for the
          month — switching tabs filters the view; it does not delete other companies. Salary slips are not generated
          for these months.
        </p>
      );
    }
    if (periodStatus === "future" || periodMeta.isFuture) {
      return (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Future months are not generated. Select the current month or a past month (from{" "}
          {getMonthLabel(FINANCE_SHEET_START.month)} {FINANCE_SHEET_START.year} onward).
        </p>
      );
    }
    return null;
  };

  const renderFilters = () => (
    <div className="finance-filters">
      <div>
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Year</label>
        <Select
          className="min-w-[120px]"
          value={filterYear}
          onChange={handleYearChange}
          options={yearOptions}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Month</label>
        <Select
          className="min-w-[140px]"
          value={filterMonth}
          onChange={setFilterMonth}
          options={monthOptions}
        />
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Employee Search</label>
        <input
          type="search"
          value={employeeSearch}
          onChange={(e) => setEmployeeSearch(e.target.value)}
          placeholder="Search by employee name"
          className="search-input w-full !text-black"
        />
      </div>
    </div>
  );

  const renderSummaryCards = (summary) => (
    <div className="finance-stat-grid mb-6">
      <div className="finance-stat-card">
        <div className="label">Total Salaries (Monthly)</div>
        <div className="value">{formatCurrency(summary.totalSalaries)}</div>
      </div>
      <div className="finance-stat-card">
        <div className="label">Total Gross Payout</div>
        <div className="value">{formatCurrency(summary.totalGross)}</div>
      </div>
      <div className="finance-stat-card">
        <div className="label">Total TDS Deducted</div>
        <div className="value">{formatCurrency(summary.totalTds)}</div>
      </div>
      <div className="finance-stat-card">
        <div className="label">Total Professional Tax</div>
        <div className="value">{formatCurrency(summary.totalProfessionalTax)}</div>
      </div>
      <div className="finance-stat-card">
        <div className="label">Total Deductions</div>
        <div className="value">{formatCurrency(summary.totalDeductions)}</div>
      </div>
      <div className="finance-stat-card">
        <div className="label">Net Monthly Payout</div>
        <div className="value">{formatCurrency(summary.netPayout)}</div>
      </div>
    </div>
  );

  const renderSheetTable = () => (
    <div className="finance-sheet-table-wrap">
      <table className="finance-sheet-table">
        <thead>
          <tr>
            {visibleSheetColumns.map((col) => (
              <th key={col} title={col === "PT" ? "Professional Tax — toggle per employee" : undefined}>
                {col === "PT" ? (
                  <>PT</>
                ) : col === "Minimum Rate of Salary / Month (Basic)" ? (
                  <>
                    Minimum Rate of Salary / Month
                    <br />
                    (Basic)
                  </>
                ) : (
                  col
                )}
              </th>
            ))}
            {isManualPeriod && canDelete ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {filteredEntries.map((row, index) => (
            <tr key={`${financePeriodKey}-${row.id}`}>
              <td>{index + 1}</td>
              <td>{row.employeeName}</td>
              <td className="finance-pt-toggle-cell">
                {isTraineeDesignation(row.designation) ? (
                  <span className="text-[#9aa8c0]">—</span>
                ) : canUpdate ? (
                  <div className="flex flex-col items-center gap-0.5">
                    <Switch
                      size="small"
                      checked={isEntryProfessionalTaxEnabled(row)}
                      disabled={Boolean(ptSavingByEntry[getEntryUiKey(row)])}
                      onChange={(checked) => toggleEntryProfessionalTax(row, checked)}
                    />
                    <span className="text-[10px] text-[#6f7d97]">
                      {ptSavingByEntry[getEntryUiKey(row)]
                        ? "…"
                        : isEntryProfessionalTaxEnabled(row)
                          ? formatCurrency(PROFESSIONAL_TAX_MONTHLY)
                          : "Off"}
                    </span>
                  </div>
                ) : (
                  <span className="text-xs text-[#6f7d97]">
                    {isEntryProfessionalTaxEnabled(row)
                      ? formatCurrency(row.professionalTax)
                      : "Off"}
                  </span>
                )}
              </td>
              <td>{formatCurrency(row.monthlySalary)}</td>
              <td>{formatCurrency(getSheetDisplayBasicSalary(row))}</td>
              <td>{row.designation || "-"}</td>
              <td title="Weekdays minus public holidays (weekends excluded)">
                {effectiveWorkingDaysForMonth || row.totalWorkingDays}
              </td>
              <td>
                {canUpdate ? (
                  <input
                    key={`${getEntryUiKey(row)}-leave-${getSheetLeaveInputValue(row)}`}
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={getSheetLeaveInputValue(row)}
                    onBlur={(e) => updateEntryField(row, "leaveDays", e.target.value)}
                  />
                ) : (
                  getSheetLeaveInputValue(row)
                )}
              </td>
              {!isManualPeriod ? (
                <td>
                  {canUpdate ? (
                    <input
                      key={`${getEntryUiKey(row)}-worked-${row.workedDays}`}
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={row.workedDays}
                      onBlur={(e) => updateEntryField(row, "workedDays", e.target.value)}
                    />
                  ) : (
                    row.workedDays
                  )}
                </td>
              ) : null}
              <td>
                {isManualPeriod && canUpdate ? (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={row.leaveDeduction}
                    onBlur={(e) => updateEntryField(row, "leaveDeduction", e.target.value)}
                  />
                ) : (
                  formatCurrency(row.leaveDeduction)
                )}
              </td>
              <td>
                {isTraineeDesignation(row.designation)
                  ? formatCurrency(row.monthlySalary)
                  : formatCurrency(getSheetDisplayBasicSalary(row))}
              </td>
              <td>
                {isTraineeDesignation(row.designation) ? (
                  <span className="text-[#9aa8c0]">—</span>
                ) : (
                  formatCurrency(row.accommodation)
                )}
              </td>
              <td>{formatCurrency(row.grossEarnings)}</td>
              <td>
                {isTraineeDesignation(row.designation) ? (
                  <span className="text-[#9aa8c0]">—</span>
                ) : (
                  formatCurrency(row.professionalTax)
                )}
              </td>
              <td>
                {isTraineeDesignation(row.designation) ? (
                  <span className="text-[#9aa8c0]">—</span>
                ) : canUpdate ? (
                  <div className="flex flex-col gap-1 min-w-[150px]">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={tdsInputsByEntry[getEntryUiKey(row)] ?? ""}
                      disabled={Boolean(tdsSavingByEntry[getEntryUiKey(row)])}
                      onChange={(e) =>
                        setTdsInputsByEntry((prev) => ({
                          ...prev,
                          [getEntryUiKey(row)]: e.target.value,
                        }))
                      }
                      placeholder={`${toNumber(row.tds).toFixed(2)}`}
                    />
                    {tdsInputsByEntry[getEntryUiKey(row)] !== undefined &&
                      tdsInputsByEntry[getEntryUiKey(row)] !== "" ? (
                      <button
                        type="button"
                        className="text-xs text-[#2b6cb0] text-left"
                        disabled={Boolean(tdsSavingByEntry[getEntryUiKey(row)])}
                        onClick={() => updateEntryTds(row)}
                      >
                        {tdsSavingByEntry[getEntryUiKey(row)] ? "Saving..." : "Save"}
                      </button>
                    ) : (
                      <span className="text-xs text-[#5f6d8a]">{formatCurrency(row.tds)}</span>
                    )}
                    {isEligibleForAutoTds(row) ? (
                      <button
                        type="button"
                        className="text-xs text-[#5f6d8a] text-left"
                        disabled={Boolean(tdsSavingByEntry[getEntryUiKey(row)])}
                        onClick={() => {
                          setTdsInputsByEntry((prev) => ({ ...prev, [getEntryUiKey(row)]: "" }));
                          updateEntryTds(row, "");
                        }}
                      >
                        Reset Auto
                      </button>
                    ) : null}
                  </div>
                ) : (
                  formatCurrency(row.tds)
                )}
              </td>
              <td>{formatCurrency(row.totalDeduction)}</td>
              <td className="font-semibold text-[#27314a]">{formatCurrency(row.netSalary)}</td>
              <td>
                {(() => {
                  const rowUiKey = getEntryUiKey(row);
                  const statusSaving = Boolean(statusSavingByEntry[rowUiKey]);
                  const displayStatus =
                    pendingStatusByEntry[rowUiKey] ??
                    (normalizeSalaryEntryStatus(row.status) || SALARY_ENTRY_STATUS.PENDING);
                  const showStatusSelect =
                    canUpdate && !isSalaryEntryPaid(row.status) && !statusSaving;

                  if (statusSaving) {
                    return (
                      <span
                        className="loading-indicator salary-entry-status-loading"
                        aria-live="polite"
                        aria-busy="true"
                      >
                        <Spin size="small" />
                        <span>Updating…</span>
                      </span>
                    );
                  }

                  if (showStatusSelect) {
                    return (
                      <select
                        className={getSalaryEntryStatusSelectClass(displayStatus)}
                        value={displayStatus}
                        onChange={(e) => updateEntryField(row, "status", e.target.value)}
                      >
                        {manualSheetStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    );
                  }

                  return (
                    <span className={getSalaryEntryStatusBadgeClass(row.status)}>
                      {getSalaryEntryStatusLabel(row.status)}
                    </span>
                  );
                })()}
              </td>
              {isManualPeriod && canDelete ? (
                <td>
                  {!isSalaryEntryPaid(row.status) ? (
                    <button
                      type="button"
                      className="text-xs text-red-600"
                      onClick={() => setEntryToDelete(row)}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="text-xs text-[#9aa8c0]">—</span>
                  )}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {!filteredEntries.length ? (
        <p className="p-6 text-center text-sm text-[#6f7d97]">
          {isSheetLoading ? "Loading salary sheet…" : "No salary sheet entries for this period."}
        </p>
      ) : null}
    </div>
  );

  const renderDashboard = () => (
    <section className="card-border card-body">
      {renderFilters()}
      {renderSalaryCompanyTabs()}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[#6f7d97] m-0">
          {getMonthLabel(filterMonth)} {filterYear}
          {activeSheet?.status ? ` · ${activeSheet.status}` : ""}
          {activeCompanyLabel ? ` · ${activeCompanyLabel}` : ""}
        </p>
        {showRefreshCalculationsButton ? (
          <button
            type="button"
            onClick={handleRefreshSheetCalculations}
            className="finance-action-btn"
            disabled={syncingSheet || loading || isSheetLoading}
            title="Recalculate leave days and deductions for all employees on this sheet"
          >
            {syncingSheet ? "Refreshing…" : "Refresh calculations"}
          </button>
        ) : null}
      </div>
      {isSheetLoading ? (
        <p className="text-sm text-[#6f7d97] py-4">Loading finance summary…</p>
      ) : (
        <>
          {renderSummaryCards(sheetSummary)}
          <p className="text-sm text-[#6f7d97] mt-2">
            {sheetSummary.employeeCount
              ? `Company summary: ${sheetSummary.employeeCount} employee payout(s) for ${activeCompanyLabel || "this company"} in ${getMonthLabel(filterMonth)} ${filterYear}.`
              : activeSheet
                ? `No employees on the salary sheet for ${activeCompanyLabel || "this company"} this month. Switch tabs or assign salary companies on employee profiles.`
                : `No salary sheet for ${getMonthLabel(filterMonth)} ${filterYear} yet.`}
          </p>
        </>
      )}
    </section>
  );

  const renderSalaryManagement = () => (
    <>
      <div className="mb-4 flex justify-between items-start gap-3">
        {canCreate && !showStructureForm ? (
          <button
            type="button"
            onClick={openCreateStructure}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg"
          >
            Add Employee Salary
          </button>
        ) : null}
      </div>

      {showStructureForm ? (
        <form onSubmit={saveStructure} className="card-border card-body mb-6 space-y-4">
          <h2 className="text-lg font-semibold text-[#27314a]">
            {editingStructureId ? "Edit Salary Structure" : "Add Salary Structure"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Employee</label>
              <Select
                className="w-full"
                options={employeeOptionsForActiveCompany}
                value={structureForm.userId || undefined}
                onChange={(value) => setStructureForm((c) => ({ ...c, userId: value || "" }))}
                placeholder={
                  employeeOptionsForActiveCompany.length
                    ? "Select employee"
                    : "No employees assigned to this company"
                }
                showSearch
                disabled={Boolean(editingStructureId)}
                optionFilterProp="label"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Designation</label>
              <input
                className="search-input w-full !text-black"
                value={structureForm.designation}
                onChange={(e) => setStructureForm((c) => ({ ...c, designation: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                {isTraineeDesignation(structureForm.designation) ? "Stipend Per Month" : "Salary Per Month"}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="search-input w-full !text-black"
                value={structureForm.monthlySalary}
                onChange={(e) => setStructureForm((c) => ({ ...c, monthlySalary: e.target.value }))}
              />
            </div>
          </div>
          {(() => {
            const isTrainee = isTraineeDesignation(structureForm.designation);
            if (isTrainee) {
              const stipend = toNumber(structureForm.monthlySalary);
              return (
                <div className="rounded-lg border border-[#e6ebf6] p-4 bg-[#f9fbff] text-sm text-[#27314a]">
                  <p className="mb-2 font-medium">Stipend calculation (trainee / intern)</p>
                  <p>
                    Stipend (monthly): <strong>{formatCurrency(stipend)}</strong>
                  </p>
                  <p className="mt-2 text-[#5f6d8a]">
                    No auto salary breakup, professional tax, or TDS. Leave beyond paid balance is deducted
                    from stipend.
                  </p>
                </div>
              );
            }
            const breakdown = getSalaryStructureBreakdown(structureForm.designation, structureForm.monthlySalary);
            return (
              <div className="rounded-lg border border-[#e6ebf6] p-4 bg-[#f9fbff] text-sm text-[#27314a]">
                <p className="mb-2 font-medium">Auto salary breakup</p>
                <p>Basic (50%): {formatCurrency(breakdown.basicSalary)}</p>
                <p>HRA (40% of Basic): {formatCurrency(breakdown.hra)}</p>
                <p>Conveyance Allowance (Fixed): {formatCurrency(breakdown.conveyanceAllowance)}</p>
                <p>Special Allowance (Remaining): {formatCurrency(breakdown.specialAllowance)}</p>
                <p className="mt-2 text-[#5f6d8a]">
                  Computed gross: <strong>{formatCurrency(calculateGrossFromStructure(breakdown))}</strong>
                </p>
              </div>
            );
          })()}
          <div className="flex gap-2">
            <button type="button" className="button px-4 py-2 rounded-lg border" onClick={() => setShowStructureForm(false)}>
              Cancel
            </button>
            <button type="submit" className="button bg-btn-primary text-white px-6 py-2 rounded-lg">
              Save Structure
            </button>
          </div>
        </form>
      ) : null}

      <section className="card-border card-body">
        {renderSalaryCompanyTabs({ showAddCompany: false })}
        <h2 className="text-lg font-semibold text-[#27314a] mb-2">Salary Structures</h2>
        <p className="text-sm text-[#6f7d97] mb-4">
          {activeCompanyLabel || "Company"} ·{" "}
          {filteredStructures.length
            ? `${activeFilteredStructures.length} active employee(s)${
              removedStructuresCount ? ` · ${removedStructuresCount} removed` : ""
            } · Total monthly payroll: ${formatCurrency(structurePayrollTotal)}`
            : "No employees assigned to this company"}
        </p>
        {canDelete ? (
          <p className="text-xs text-[#6f7d97] mb-4">
            Use <strong>Remove</strong> when an employee has left. We use their last working day (or you select the last
            working month) and drop them from later salary sheets. The last working month sheet stays.
          </p>
        ) : null}
        {filteredStructures.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#27314a] border-b">
                  <th className="py-2 pr-3">Employee</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Salary company</th>
                  <th className="py-2 pr-3">Designation</th>
                  <th className="py-2 pr-3">Monthly Salary</th>
                  <th className="py-2 pr-3">Basic</th>
                  <th className="py-2 pr-3">HRA</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStructures.map((structure) => (
                  <tr key={structure.id} className="border-b border-[#eef2f9] text-[#27314a]">
                    <td className="py-2 pr-3">
                      {`${structure.firstname || ""} ${structure.lastname || ""}`.trim()}
                    </td>
                    <td className="py-2 pr-3">
                      {Number(structure.isDelete) === 1 ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                            Removed
                          </span>
                          {getStructureRemovedFromLabel(
                            structure.lastWorkingDate || lastWorkingDateByUserId.get(Number(structure.userId))
                          ) ? (
                            <span className="text-[11px] text-[#6f7d97]">
                              From{" "}
                              {getStructureRemovedFromLabel(
                                structure.lastWorkingDate || lastWorkingDateByUserId.get(Number(structure.userId))
                              )}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {getFinanceSalaryCompanyLabelSync(structure.salaryCompanyId, companyProfiles) ||
                        DEFAULT_SALARY_COMPANY_NAME}
                    </td>
                    <td className="py-2 pr-3">{structure.designation || "-"}</td>
                    <td className="py-2 pr-3">{formatCurrency(structure.monthlySalary)}</td>
                    <td className="py-2 pr-3">
                      {isTraineeDesignation(structure.designation)
                        ? formatCurrency(structure.monthlySalary)
                        : formatCurrency(structure.basicSalary)}
                    </td>
                    <td className="py-2 pr-3">
                      {isTraineeDesignation(structure.designation) ? "—" : formatCurrency(structure.hra)}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        {canUpdate && Number(structure.isDelete) !== 1 ? (
                          <button
                            type="button"
                            className="text-xs border rounded px-2 py-1"
                            onClick={() => openEditStructure(structure)}
                          >
                            Edit
                          </button>
                        ) : null}
                        {canDelete && Number(structure.isDelete) !== 1 ? (
                          <button
                            type="button"
                            className="text-xs border border-red-200 text-red-600 rounded px-2 py-1"
                            disabled={structureDeleteLoading}
                            onClick={() =>
                              setStructureToDelete({
                                ...structure,
                                lastWorkingDate:
                                  structure.lastWorkingDate ||
                                  lastWorkingDateByUserId.get(Number(structure.userId)) ||
                                  null,
                              })
                            }
                          >
                            {structureDeleteLoading && structureToDelete?.id === structure.id ? "Removing..." : "Remove"}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : structures.length ? (
          <p className="text-sm text-[#6f7d97] py-4 text-center">
            No employees for {activeCompanyLabel || "this company"}. Assign employees to this company
            in their profile (Salary company), or switch to another tab.
          </p>
        ) : (
          <p className="text-sm text-[#6f7d97]">No salary structures configured yet.</p>
        )}
      </section>
    </>
  );

  const manualSheetStatusOptions = SALARY_ENTRY_STATUS_OPTIONS;

  const visibleSheetColumns = useMemo(
    () =>
      isManualPeriod
        ? SHEET_COLUMNS.filter((col) => col !== "Total Worked Days")
        : SHEET_COLUMNS,
    [isManualPeriod]
  );

  const templateImportEmployees = useMemo(() => {
    const sheetEntryUserIds = new Set(
      (activeSheet?.entries || [])
        .filter((row) => row.userId != null)
        .map((row) => String(row.userId))
    );
    return (Array.isArray(sheetImportEmployees) ? sheetImportEmployees : [])
      .filter((employee) => Number(employee.isDelete) === 0 && !isCeoOrCmoRoleUser(employee))
      .filter((employee) =>
        matchesActiveSalaryCompanyTab(
          { userId: employee.id, salaryCompanyId: employee.salaryCompanyId },
          activeSalaryCompanyId,
          companyProfiles,
          salaryCompanyByUserId
        )
      )
      .filter((employee) => !sheetEntryUserIds.has(String(employee.id)));
  }, [
    activeSheet,
    sheetImportEmployees,
    activeSalaryCompanyId,
    companyProfiles,
    salaryCompanyByUserId,
  ]);

  const handleDownloadImportTemplate = () => {
    if (!templateImportEmployees.length) {
      showErrorToast(
        "No employees to include. Switch company tab or add missing staff under User Management first."
      );
      return;
    }
    downloadManualSheetImportTemplate({
      employees: templateImportEmployees,
      structures,
      sheetLabel: `${getMonthLabel(filterMonth)} ${filterYear}`,
    });
  };

  const manualSheetImportOptions = useMemo(() => {
    const sheetEntryUserIds = new Set(
      (activeSheet?.entries || [])
        .filter((row) => row.userId != null)
        .map((row) => String(row.userId))
    );
    return (Array.isArray(sheetImportEmployees) ? sheetImportEmployees : [])
      .filter((employee) => !isCeoOrCmoRoleUser(employee))
      .filter((employee) =>
        matchesActiveSalaryCompanyTab(
          { userId: employee.id, salaryCompanyId: employee.salaryCompanyId },
          activeSalaryCompanyId,
          companyProfiles,
          salaryCompanyByUserId
        )
      )
      .filter((employee) => !sheetEntryUserIds.has(String(employee.id)))
      .map((employee) => {
        const label = `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email;
        const inactive = Number(employee.isDelete) === 1 ? " (removed)" : "";
        return { value: String(employee.id), label: `${label}${inactive}` };
      });
  }, [
    activeSheet,
    sheetImportEmployees,
    activeSalaryCompanyId,
    companyProfiles,
    salaryCompanyByUserId,
  ]);

  const renderManualAddEmployeeToolbar = () => {
    if (!isManualPeriod || !activeSheet || !canCreate) return null;
    return (
      <div className="finance-manual-toolbar mb-4 flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={openManualEntryForm}
          className="finance-action-btn"
          disabled={showManualEntryForm}
        >
          + Add employee
        </button>
        {showManualEntryForm ? (
          <button
            type="button"
            className="finance-action-btn"
            onClick={() => setShowManualEntryForm(false)}
          >
            Cancel
          </button>
        ) : null}
      </div>
    );
  };

  const renderBulkImportPanel = () => {
    if (!isManualPeriod || !activeSheet || !canCreate) return null;
    if (filteredEntries.length > 0) return null;
    return (
      <div className="finance-bulk-import mb-6 rounded-xl border border-[#e6ebf6] bg-[#f9fbff] p-4">
        <h3 className="text-base font-semibold text-[#27314a] mb-2">Bulk upload from Excel</h3>
        <p className="text-sm text-[#6f7d97] mb-3">
          Download the template — it lists all active employees for this company ({templateImportEmployees.length}{" "}
          rows). Edit salaries and days, then upload. For anyone not in the file, use{" "}
          <strong>Add employee</strong> with their name and optional <strong>User Id</strong>.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <button type="button" className="finance-action-btn" onClick={handleDownloadImportTemplate}>
            Download template ({templateImportEmployees.length} employees)
          </button>
          <button
            type="button"
            className="finance-action-btn"
            disabled={bulkImporting}
            onClick={() => bulkImportInputRef.current?.click()}
          >
            {bulkImporting ? "Importing…" : "Upload Excel"}
          </button>
          <input
            ref={bulkImportInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleBulkImportFile}
          />
        </div>
      </div>
    );
  };

  const renderManualEntryForm = () => {
    if (!showManualEntryForm || !activeSheet) return null;

    return (
      <form className="finance-manual-entry-form mb-6" onSubmit={saveManualEntry}>
        <h3 className="text-base font-semibold text-[#27314a] mb-3">Add employee to sheet</h3>
        <div className="flex flex-wrap gap-4 mb-4">
          <label className="inline-flex items-center gap-2 text-sm text-[#27314a]">
            <input
              type="radio"
              name="manualEntryMode"
              checked={manualEntryMode === "employee"}
              onChange={() => setManualEntryMode("employee")}
              className="w-4 h-4 p-0"
            />
            From employee list (active or inactive)
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-[#27314a]">
            <input
              type="radio"
              name="manualEntryMode"
              checked={manualEntryMode === "former"}
              onChange={() => setManualEntryMode("former")}
              className="w-4 h-4 p-0"
            />
            Previous employee (not in list)
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {manualEntryMode === "employee" ? (
            <div>
              <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Employee</label>
              <Select
                className="w-full"
                placeholder="Select employee"
                value={manualEntryForm.userId || undefined}
                onChange={onManualEntryEmployeeChange}
                options={manualSheetImportOptions}
                showSearch
                optionFilterProp="label"
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Employee name</label>
                <input
                  type="text"
                  className="search-input w-full !text-black"
                  value={manualEntryForm.employeeName}
                  onChange={(e) =>
                    setManualEntryForm((prev) => ({ ...prev, employeeName: e.target.value }))
                  }
                  placeholder="Full name as on old payroll"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
                  User Id <span className="text-[#9aa8c0]">(optional)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  className="search-input w-full !text-black"
                  value={manualEntryForm.manualUserId}
                  onChange={(e) =>
                    setManualEntryForm((prev) => ({ ...prev, manualUserId: e.target.value }))
                  }
                  placeholder="If they exist in the system but are not in the list"
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Designation</label>
            <input
              type="text"
              className="search-input w-full !text-black"
              value={manualEntryForm.designation}
              onChange={(e) => setManualEntryForm((prev) => ({ ...prev, designation: e.target.value }))}
            />
          </div>
          {!isTraineeDesignation(manualEntryForm.designation) ? (
            <div className="flex items-center gap-2 pt-6">
              <Switch
                checked={manualEntryForm.professionalTaxEnabled}
                onChange={(checked) =>
                  setManualEntryForm((prev) => ({ ...prev, professionalTaxEnabled: checked }))
                }
              />
              <span className="text-sm text-[#5f6d8a]">
                Professional Tax ({formatCurrency(PROFESSIONAL_TAX_MONTHLY)})
              </span>
            </div>
          ) : null}
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
              Monthly salary
              {isManualPeriod ? (
                <span className="text-[#9aa8c0] font-normal"> (historical — not current salary)</span>
              ) : null}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.monthlySalary}
              onChange={(e) => {
                const nextMonthly = e.target.value;
                setManualEntryForm((prev) => {
                  if (nextMonthly === "" || nextMonthly == null) {
                    return { ...prev, monthlySalary: nextMonthly, basicSalary: "", accommodation: "" };
                  }
                  const breakdown = getSalaryStructureBreakdown(prev.designation, toNumber(nextMonthly));
                  return {
                    ...prev,
                    monthlySalary: nextMonthly,
                    basicSalary: String(breakdown.basicSalary),
                    accommodation: String(breakdown.hra),
                  };
                });
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Basic</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.basicSalary}
              onChange={(e) => setManualEntryForm((prev) => ({ ...prev, basicSalary: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Accommodation (HRA)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.accommodation}
              onChange={(e) => setManualEntryForm((prev) => ({ ...prev, accommodation: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Leaves</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.leaveDays}
              onChange={(e) => setManualEntryForm((prev) => ({ ...prev, leaveDays: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Leaves deduction</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.leaveDeduction}
              onChange={(e) =>
                setManualEntryForm((prev) => ({ ...prev, leaveDeduction: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">TDS (optional)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="search-input w-full !text-black"
              value={manualEntryForm.tds}
              onChange={(e) => setManualEntryForm((prev) => ({ ...prev, tds: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button type="submit" className="finance-action-btn" disabled={manualEntrySaving}>
            {manualEntrySaving ? "Saving…" : "Add to sheet"}
          </button>
          <button
            type="button"
            className="finance-action-btn"
            onClick={() => setShowManualEntryForm(false)}
            disabled={manualEntrySaving}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  const renderCreateManualSheetPanel = () => (
    <div className="finance-manual-create-panel py-6 text-center">
      <p className="text-sm text-[#6f7d97] mb-4">
        No salary sheet for {getMonthLabel(filterMonth)} {filterYear}. Create a manual sheet to enter payroll for
        this historical month. Import each company on its own tab — data from other tabs is kept on the same sheet.
      </p>
      <div className="max-w-xs mx-auto mb-4 text-left">
        <label className="block text-sm font-medium text-[#5f6d8a] mb-1">Total working days</label>
        <input
          type="number"
          min="1"
          className="search-input w-full !text-black"
          value={manualSheetWorkingDays}
          onChange={(e) => setManualSheetWorkingDays(e.target.value)}
        />
      </div>
      {canCreateManualSheet ? (
        <button
          type="button"
          className="finance-action-btn"
          onClick={createManualSalarySheet}
          disabled={manualSheetCreating}
        >
          {manualSheetCreating ? "Creating…" : "Create manual salary sheet"}
        </button>
      ) : (
        <p className="text-sm text-[#9aa8c0]">You do not have permission to create salary sheets.</p>
      )}
    </div>
  );

  const renderSheetActionButtons = () => (
    <div className="finance-action-buttons">
      {showRefreshCalculationsButton ? (
        <button
          type="button"
          onClick={handleRefreshSheetCalculations}
          className="finance-action-btn"
          disabled={syncingSheet || loading || isSheetLoading}
          title="Recalculate leave days and deductions for all employees on this sheet"
        >
          {syncingSheet ? "Refreshing…" : "Refresh calculations"}
        </button>
      ) : null}
      <button
        type="button"
        onClick={exportExcel}
        className="finance-action-btn"
        disabled={!filteredEntries.length}
      >
        Export Excel
      </button>
      <button
        type="button"
        onClick={exportPdf}
        className="finance-action-btn"
        disabled={!filteredEntries.length}
      >
        Export PDF
      </button>
      {activeSheet && canDelete && canDeleteSheetForPeriod && activeTabSalaryCompanyId && filteredEntries.length ? (
        <button
          type="button"
          onClick={() =>
            setCompanyDataToClear({
              sheetId: activeSheet.id,
              salaryCompanyId: activeTabSalaryCompanyId,
              companyLabel: activeCompanyLabel,
              entryCount: filteredEntries.length,
            })
          }
          className="finance-action-btn finance-action-btn--danger"
        >
          Delete {activeCompanyLabel || "company"} data
        </button>
      ) : null}
      {/* {activeSheet && canDelete && (activeSheet?.entries?.length || 0) > 0 ? (
        <button
          type="button"
          onClick={() => setSheetToDelete(activeSheet)}
          className="finance-action-btn finance-action-btn--danger"
          title="Removes every company for this month"
        >
          Delete entire month
        </button>
      ) : null} */}
    </div>
  );

  const renderMonthlySheets = () => (
    <section className="card-border card-body">
      {renderFilters()}
      {renderSalaryCompanyTabs()}
      {renderPeriodNotice()}
      <div className="mb-4">
        <label className="block text-xs text-[#6f7d97] mb-1">
          Total Working Days <span className="text-[#9aa8c0]">(Mon–Fri minus public holidays, weekends excluded)</span>
        </label>
        <p className="text-lg font-semibold text-[#27314a]">{effectiveWorkingDaysForMonth}</p>
        <p className="mt-1 text-xs text-[#9aa8c0]">
          {getMonthLabel(filterMonth)} {filterYear} — weekdays minus public holidays
        </p>
      </div>

      {isSheetLoading ? (
        <p className="text-sm text-[#6f7d97] py-8 text-center">Loading monthly salary sheet…</p>
      ) : periodStatus === "too_early" || periodStatus === "future" ? (
        <p className="text-sm text-[#6f7d97] py-6 text-center">
          {periodStatus === "future"
            ? "Future months are not available for salary sheets."
            : `Choose a month from ${getMonthLabel(MANUAL_SHEET_MIN.month)} ${MANUAL_SHEET_MIN.year} onward.`}
        </p>
      ) : !activeSheet && isManualPeriod ? (
        renderCreateManualSheetPanel()
      ) : !activeSheet ? (
        <p className="text-sm text-[#6f7d97] py-6 text-center">
          No salary sheet for this period. Sheets from {getMonthLabel(FINANCE_SHEET_START.month)}{" "}
          {FINANCE_SHEET_START.year} are created automatically when you open the page.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-[#6f7d97] m-0">
              {getMonthLabel(filterMonth)} {filterYear}
              {isManualPeriod ? " · Manual sheet" : ""}
              {activeSheet?.status ? ` · ${activeSheet.status}` : ""}
              {activeCompanyLabel ? ` · ${activeCompanyLabel}` : ""} · Net: Gross − Leave Deduction −
              Professional Tax ({formatCurrency(PROFESSIONAL_TAX_MONTHLY)}) − TDS · Trainees / interns: Stipend −
              Leave Deduction only
            </p>
            {showRefreshCalculationsButton ? (
              <button
                type="button"
                onClick={handleRefreshSheetCalculations}
                className="finance-action-btn"
                disabled={syncingSheet || loading || isSheetLoading}
                title="Recalculate leave days and deductions for all employees on this sheet"
              >
                {syncingSheet ? "Refreshing…" : "Refresh calculations"}
              </button>
            ) : null}
          </div>
          {renderManualAddEmployeeToolbar()}
          {renderManualEntryForm()}
          {renderBulkImportPanel()}
          {renderSummaryCards(sheetSummary)}
          {filteredEntries.length ? (
            renderSheetTable()
          ) : (
            <p className="text-sm text-[#6f7d97] py-6 text-center">
              {searchedEntries.length
                ? `No employees for ${activeCompanyLabel || "this company"} on this sheet. Try another company tab or assign salary companies on employee profiles.`
                : isManualPeriod
                  ? "No employees on this sheet yet. Use Add employee to enter payroll for this month."
                  : "No employees on this sheet yet. Add salary structures under Salary Management — they will appear here automatically."}
            </p>
          )}
          {renderSheetActionButtons()}
        </>
      )}
    </section>
  );

  const renderRecords = () => {
    const records = filteredStructures.filter((s) => {
      const name = `${s.firstname} ${s.lastname}`.toLowerCase();
      return !employeeSearch.trim() || name.includes(employeeSearch.trim().toLowerCase());
    });
    const activeRecords = records.filter((structure) => !isRemovedStructure(structure));
    const removedRecordsCount = records.length - activeRecords.length;

    return (
      <section className="card-border card-body">
        <h2 className="text-lg font-semibold text-[#27314a] mb-4">Employee Salary Records</h2>
        {renderFilters()}
        {renderSalaryCompanyTabs()}
        <p className="text-sm text-[#6f7d97] mb-4">
          {activeCompanyLabel || "Company"} ·{" "}
          {records.length
            ? `${activeRecords.length} active record(s)${
              removedRecordsCount ? ` · ${removedRecordsCount} removed` : ""
            } · Total monthly: ${formatCurrency(
              activeRecords.reduce((sum, row) => sum + toNumber(row.monthlySalary), 0)
            )}`
            : "No salary records for this company"}
        </p>
        {records.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[#27314a]">
            {records.map((structure) => {
              const removed = isRemovedStructure(structure);
              const removedFromLabel = removed
                ? getStructureRemovedFromLabel(
                  structure.lastWorkingDate || lastWorkingDateByUserId.get(Number(structure.userId))
                )
                : "";
              return (
              <article
                key={structure.id}
                className={`rounded-xl border p-4 ${
                  removed ? "border-rose-200 bg-rose-50/40 opacity-80" : "border-[#e6ebf6] bg-[#f9fbff]"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-[#27314a]">
                    {`${structure.firstname || ""} ${structure.lastname || ""}`.trim()}
                  </h3>
                  {removed ? (
                    <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-700">
                      {removedFromLabel ? `Removed from ${removedFromLabel}` : "Removed"}
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-[#6f7d97] mb-1">
                  {getFinanceSalaryCompanyLabelSync(structure.salaryCompanyId, companyProfiles) ||
                    DEFAULT_SALARY_COMPANY_NAME}
                </p>
                <p className="text-xs text-[#6f7d97] mb-2">{structure.designation || "—"}</p>
                {isTraineeDesignation(structure.designation) ? (
                  <div className="text-sm">
                    <span>Stipend: {formatCurrency(structure.monthlySalary)}</span>
                    <p className="mt-2 text-xs text-[#6f7d97]">
                      No salary breakup, professional tax, or TDS. Unpaid leave is deducted from stipend.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span>Monthly: {formatCurrency(structure.monthlySalary)}</span>
                    <span>Basic: {formatCurrency(structure.basicSalary)}</span>
                    <span>HRA: {formatCurrency(structure.hra)}</span>
                    <span>Conveyance: {formatCurrency(Math.min(1600, Math.max(0, toNumber(structure.allowances))))}</span>
                    <span>
                      Special:{" "}
                      {formatCurrency(
                        Math.max(
                          0,
                          toNumber(structure.allowances) -
                          Math.min(1600, Math.max(0, toNumber(structure.allowances)))
                        )
                      )}
                    </span>
                  </div>
                )}
              </article>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-[#6f7d97] py-6 text-center">
            {structures.length
              ? `No salary records for ${activeCompanyLabel || "this company"}. Assign employees to this company in their profile.`
              : "No salary structures configured yet. Add them under Salary Management."}
          </p>
        )}
      </section>
    );
  };

  const renderDeductions = () => (
    <section className="card-border card-body space-y-4">
      <h2 className="text-lg font-semibold text-[#27314a]">Deductions & Taxes</h2>
      <div className="finance-policy-box">
        <p>
          <strong>Professional Tax:</strong> Fixed {formatCurrency(PROFESSIONAL_TAX_MONTHLY)} per employee per
          month (auto-applied).
        </p>
        <p>
          <strong>TDS:</strong> Auto-calculated from salary slabs using annual salary (monthly salary × 12). No
          TDS up to ₹12,00,000 LPA; above that, slab rate applies on actual monthly basic salary (pro-rated by
          worked days). You can override TDS manually per employee using monthly or annual mode.
        </p>
        <p>
          <strong>Leave Policy:</strong> {PAID_LEAVES_PER_YEAR} paid leaves per year, accruing{" "}
          {MONTHLY_PAID_LEAVE_ACCRUAL} per month (April–March). Only accrued balance through the sheet month is
          used—future months are not counted. Extra leave in that month is deducted from net salary.
        </p>
        <p>
          <strong>Total Working Days:</strong> Weekdays only (Saturday and Sunday excluded) for the selected
          month.
        </p>
        <p>
          <strong>Leave Deduction:</strong> Per Day Salary = Monthly Salary ÷ Total Working Days. Leave
          Deduction = Unpaid Leave Days × Per Day Salary.
        </p>
        <p>
          <strong>Net Salary:</strong> Gross Salary − Leave Deduction − Professional Tax − TDS
        </p>
        <p>
          <strong>Trainee / intern designation:</strong> Designation containing trainee, intern, or interns
          uses stipend only (no auto salary breakup). Professional tax and TDS are not applied. Net stipend =
          Stipend − Leave Deduction (when unpaid leave is taken).
        </p>
      </div>
      <div className="rounded-lg border border-[#e6ebf6] p-4 bg-white">
        <h3 className="font-medium text-[#27314a] mb-2">Example (₹30,000 salary, 30 working days)</h3>
        <p className="text-sm text-[#5f6d8a]">
          Days worked: 27 · Leaves: 3 · Paid leave remaining (YTD): 2 · Unpaid leave: 1 · Per day: ₹1,000 ·
          Leave deduction: ₹1,000 · TDS: ₹500 · Net: {formatCurrency(28300)}
        </p>
      </div>
    </section>
  );

  const renderExpensesManagement = () => (
    <section className="card-border card-body finance-expenses-section space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[#27314a]">
            {expenseSection === "office" ? "Office Expenses" : "Expenses Management"}
          </h2>
          <p className="text-sm text-[#6f7d97] mt-1">
            {expenseSection === "office"
              ? "Track office material and event expenses for the selected month."
              : "Track monthly expenses and upload supporting finance documents."}
          </p>
        </div>
        {isExpenseListSection && canCreateExpenses && !showExpenseForm ? (
          <button
            type="button"
            onClick={openCreateExpense}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg"
          >
            {expenseSection === "office" ? "Add Office Expense" : "Add Expense"}
          </button>
        ) : expenseSection === "documents" && canCreateDocuments && !showDocumentForm ? (
          <button
            type="button"
            onClick={openCreateDocument}
            className="button bg-btn-primary text-white px-6 py-2 rounded-lg"
          >
            Add Document
          </button>
        ) : null}
      </div>

      {visibleExpenseTabs.length > 1 ? (
        <nav className="finance-expenses-tabs" aria-label="Expense management sections">
          {visibleExpenseTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`finance-expenses-tab ${expenseSection === tab.key ? "active" : ""}`}
              onClick={() => handleExpenseSectionChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      ) : null}

      {expenseSection === "documents" ? (
        renderUploadDocumentsContent()
      ) : (
        <>
      {renderExpensePeriodFilters()}

      <div className="finance-stat-grid">
        <div className="finance-stat-card">
          <div className="label">Total Expenses</div>
          <div className="value">{formatCurrency(filteredExpensesTotalAmount)}</div>
        </div>
        <div className="finance-stat-card">
          <div className="label">Expense Count</div>
          <div className="value">{filteredSalaryExpenses.length}</div>
        </div>
      </div>

      {showExpenseForm ? (
        <form onSubmit={saveExpense} className="finance-expenses-form">
          <h3 className="text-base font-semibold text-[#27314a]">
            {editingExpenseId
              ? expenseSection === "office"
                ? "Edit Office Expense"
                : "Edit Expense"
              : expenseSection === "office"
                ? "Add Office Expense"
                : "Add Expense"}
          </h3>
          <div className="finance-expenses-form__grid">
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Type</span>
              <Select
                className="w-full"
                placeholder="Select expense type"
                value={expenseForm.expenseType || undefined}
                onChange={(value) =>
                  setExpenseForm((current) => ({
                    ...current,
                    expenseType: value,
                    customExpenseType: value === EXPENSE_TYPE_OTHER ? current.customExpenseType : "",
                  }))
                }
                options={expenseTypeOptionsForSection}
              />
            </label>
            {expenseForm.expenseType === EXPENSE_TYPE_OTHER ? (
              <label className="finance-expenses-field">
                <span className="finance-expenses-field__label">Other Type</span>
                <input
                  type="text"
                  value={expenseForm.customExpenseType}
                  onChange={(event) =>
                    setExpenseForm((current) => ({ ...current, customExpenseType: event.target.value }))
                  }
                  placeholder="Enter custom expense type"
                  maxLength={255}
                  className="search-input w-full !text-black"
                />
              </label>
            ) : null}
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Payment Method</span>
              <Select
                className="w-full"
                placeholder="Select payment method"
                value={expenseForm.paymentMethod || undefined}
                onChange={(value) =>
                  setExpenseForm((current) => ({
                    ...current,
                    paymentMethod: value,
                    upiId: value === "UPI" ? current.upiId : "",
                    cardUsedBy: value === "Card" ? current.cardUsedBy : "",
                  }))
                }
                options={PAYMENT_METHOD_OPTIONS}
              />
            </label>
            {expenseForm.paymentMethod === "UPI" ? (
              <>
                <label className="finance-expenses-field">
                  <span className="finance-expenses-field__label">UPI ID (optional)</span>
                  <input
                    type="text"
                    value={expenseForm.upiId}
                    onChange={(event) =>
                      setExpenseForm((current) => ({ ...current, upiId: event.target.value }))
                    }
                    placeholder="e.g. name@upi"
                    maxLength={120}
                    className="search-input w-full !text-black"
                  />
                </label>
                <label className="finance-expenses-field finance-expenses-field--full">
                  <span className="finance-expenses-field__label">UPI Screenshot</span>
                  <input
                    ref={expenseUpiScreenshotInputRef}
                    type="file"
                    accept={EXPENSE_IMAGE_ACCEPT}
                    onChange={(event) => setExpenseUpiScreenshotFile(event.target.files?.[0] || null)}
                    className="finance-expenses-file-input"
                  />
                  {expenseUpiScreenshotFile ? (
                    <span className="finance-expenses-file-hint">Selected: {expenseUpiScreenshotFile.name}</span>
                  ) : editingExpenseMeta?.hasUpiScreenshot ? (
                    <span className="finance-expenses-file-hint">
                      Current: {editingExpenseMeta.upiScreenshotFileName || "UPI screenshot on file"}
                    </span>
                  ) : null}
                </label>
              </>
            ) : null}
            {expenseForm.paymentMethod === "Card" ? (
              <label className="finance-expenses-field">
                <span className="finance-expenses-field__label">Whose Card Was Used</span>
                <input
                  type="text"
                  value={expenseForm.cardUsedBy}
                  onChange={(event) =>
                    setExpenseForm((current) => ({ ...current, cardUsedBy: event.target.value }))
                  }
                  placeholder="e.g. Employee / Company card holder"
                  maxLength={255}
                  className="search-input w-full !text-black"
                />
              </label>
            ) : null}
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Date</span>
              <input
                type="date"
                value={expenseForm.expenseDate}
                onChange={(event) =>
                  setExpenseForm((current) => ({ ...current, expenseDate: event.target.value }))
                }
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Price</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expenseForm.price}
                onChange={(event) =>
                  setExpenseForm((current) => ({ ...current, price: event.target.value }))
                }
                placeholder="0.00"
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Paid By</span>
              <input
                type="text"
                value={expenseForm.paidBy}
                onChange={(event) =>
                  setExpenseForm((current) => ({ ...current, paidBy: event.target.value }))
                }
                placeholder="e.g. Company / Employee name"
                maxLength={255}
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field finance-expenses-field--full">
              <span className="finance-expenses-field__label">Upload Invoice</span>
              <input
                ref={expenseInvoiceInputRef}
                type="file"
                accept={EXPENSE_FILE_ACCEPT}
                onChange={(event) => setExpenseInvoiceFile(event.target.files?.[0] || null)}
                className="finance-expenses-file-input"
              />
              {expenseInvoiceFile ? (
                <span className="finance-expenses-file-hint">Selected: {expenseInvoiceFile.name}</span>
              ) : editingExpenseMeta?.hasInvoice ? (
                <span className="finance-expenses-file-hint">
                  Current: {editingExpenseMeta.invoiceFileName || "Invoice on file"}
                </span>
              ) : null}
            </label>
            <label className="finance-expenses-field finance-expenses-field--full">
              <span className="finance-expenses-field__label">Description</span>
              <textarea
                value={expenseForm.description}
                onChange={(event) =>
                  setExpenseForm((current) => ({ ...current, description: event.target.value }))
                }
                placeholder="Add expense details"
                rows={3}
                maxLength={2000}
                className="search-input w-full !text-black resize-y min-h-[80px]"
              />
            </label>
          </div>
          <div className="finance-expenses-form__actions">
            <button
              type="button"
              className="finance-action-btn"
              onClick={resetExpenseForm}
              disabled={expenseSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="finance-action-btn finance-action-btn--primary"
              disabled={expenseSaving}
            >
              {expenseSaving ? "Saving…" : editingExpenseId ? "Update expense" : "Save expense"}
            </button>
          </div>
        </form>
      ) : null}

      {expensesLoading ? (
        <div className="finance-expenses-loading">
          <Spin />
        </div>
      ) : filteredSalaryExpenses.length ? (
        <div className="overflow-x-auto">
          <table className="finance-expenses-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Date</th>
                <th>Price</th>
                <th>Payment</th>
                <th>Paid By</th>
                <th>Description</th>
                <th>Attachments</th>
                <th>Added On</th>
                {(canUpdateExpenses || canDeleteExpenses) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredSalaryExpenses.map((expense) => (
                <tr key={expense.id}>
                  <td>{getExpenseTypeLabel(expense.expenseType, expense.customExpenseType) || expense.expenseName || "—"}</td>
                  <td>{formatExpenseDate(expense.expenseDate)}</td>
                  <td>{formatCurrency(expense.price)}</td>
                  <td>
                    <div className="finance-expenses-table__payment">
                      <span>{expense.paymentMethod || "—"}</span>
                      {expense.paymentMethod === "UPI" && expense.upiId ? (
                        <span className="finance-expenses-table__meta">{expense.upiId}</span>
                      ) : null}
                      {expense.paymentMethod === "Card" && expense.cardUsedBy ? (
                        <span className="finance-expenses-table__meta">{expense.cardUsedBy}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>{expense.paidBy || "—"}</td>
                  <td className="finance-expenses-table__reason">{expense.description || expense.reason || "—"}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      {expense.hasInvoice ? (
                        <button
                          type="button"
                          className="finance-action-btn"
                          onClick={() => handleViewExpenseAttachment(expense, "invoice")}
                        >
                          Invoice
                        </button>
                      ) : null}
                      {expense.hasUpiScreenshot ? (
                        <button
                          type="button"
                          className="finance-action-btn"
                          onClick={() => handleViewExpenseAttachment(expense, "upiScreenshot")}
                        >
                          UPI
                        </button>
                      ) : null}
                      {!expense.hasInvoice && !expense.hasUpiScreenshot ? "—" : null}
                    </div>
                  </td>
                  <td>{formatDocumentDate(expense.createdAt)}</td>
                  {(canUpdateExpenses || canDeleteExpenses) && (
                    <td>
                      <div className="flex flex-wrap gap-2">
                        {canUpdateExpenses ? (
                          <button
                            type="button"
                            className="finance-action-btn"
                            onClick={() => openEditExpense(expense)}
                          >
                            Edit
                          </button>
                        ) : null}
                        {canDeleteExpenses ? (
                          <button
                            type="button"
                            className="finance-action-btn finance-action-btn--danger"
                            onClick={() => setExpenseToDelete(expense)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-[#6f7d97] py-6 text-center">
          {expenseSection === "office"
            ? `No office expenses recorded for ${getMonthLabel(filterMonth)} ${filterYear}.`
            : `No expenses recorded for ${getMonthLabel(filterMonth)} ${filterYear}.`}
        </p>
      )}

      {viewingExpenseAttachment ? (
        <div className="finance-expenses-attachment-viewer">
          <div className="finance-expenses-attachment-viewer__header">
            <h3 className="text-base font-semibold text-[#27314a]">
              {viewingExpenseAttachment.attachmentType === "invoice" ? "Invoice" : "UPI Screenshot"}
            </h3>
            <button
              type="button"
              className="finance-action-btn"
              onClick={closeExpenseAttachmentViewer}
            >
              Close
            </button>
          </div>
          {expenseAttachmentViewLoading ? (
            <div className="finance-expenses-loading">
              <Spin />
            </div>
          ) : null}
          {!expenseAttachmentViewLoading &&
          expenseAttachmentViewUrl &&
          isPdfMimeType(
            viewingExpenseAttachment.attachmentType === "invoice"
              ? viewingExpenseAttachment.expense.invoiceMimeType
              : viewingExpenseAttachment.expense.upiScreenshotMimeType
          ) ? (
            <div className="finance-expenses-attachment-viewer__content">
              <CompanyPolicyPdfViewer
                key={expenseAttachmentViewUrl}
                fileUrl={expenseAttachmentViewUrl}
              />
            </div>
          ) : null}
          {!expenseAttachmentViewLoading &&
          expenseAttachmentViewUrl &&
          isImageMimeType(
            viewingExpenseAttachment.attachmentType === "invoice"
              ? viewingExpenseAttachment.expense.invoiceMimeType
              : viewingExpenseAttachment.expense.upiScreenshotMimeType
          ) ? (
            <div className="finance-expenses-attachment-viewer__content">
              <img
                src={expenseAttachmentViewUrl}
                alt={
                  viewingExpenseAttachment.attachmentType === "invoice"
                    ? "Expense invoice"
                    : "UPI screenshot"
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
        </>
      )}
    </section>
  );

  const renderUploadDocumentsContent = () => (
    <div className="finance-documents-section space-y-4">
      {renderExpensePeriodFilters()}

      <div className="finance-stat-grid">
        <div className="finance-stat-card">
          <div className="label">Total Amount</div>
          <div className="value">{formatCurrency(documentsTotalAmount)}</div>
        </div>
        <div className="finance-stat-card">
          <div className="label">Document Count</div>
          <div className="value">{financeDocuments.length}</div>
        </div>
      </div>

      {showDocumentForm ? (
        <form onSubmit={saveDocument} className="finance-expenses-form">
          <h3 className="text-base font-semibold text-[#27314a]">
            {editingDocumentId ? "Edit Document" : "Add Document"}
          </h3>
          <div className="finance-expenses-form__grid">
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Type</span>
              <Select
                className="w-full"
                placeholder="Select expense type"
                value={documentForm.expenseType || undefined}
                onChange={(value) =>
                  setDocumentForm((current) => ({
                    ...current,
                    expenseType: value,
                    customExpenseType: value === EXPENSE_TYPE_OTHER ? current.customExpenseType : "",
                  }))
                }
                options={EXPENSE_TYPE_OPTIONS}
              />
            </label>
            {documentForm.expenseType === EXPENSE_TYPE_OTHER ? (
              <label className="finance-expenses-field">
                <span className="finance-expenses-field__label">Other Type</span>
                <input
                  type="text"
                  value={documentForm.customExpenseType}
                  onChange={(event) =>
                    setDocumentForm((current) => ({ ...current, customExpenseType: event.target.value }))
                  }
                  placeholder="Enter custom expense type"
                  maxLength={255}
                  className="search-input w-full !text-black"
                />
              </label>
            ) : null}
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Payment Method</span>
              <Select
                className="w-full"
                placeholder="Select payment method"
                value={documentForm.paymentMethod || undefined}
                onChange={(value) =>
                  setDocumentForm((current) => ({
                    ...current,
                    paymentMethod: value,
                    upiId: value === "UPI" ? current.upiId : "",
                    cardUsedBy: value === "Card" ? current.cardUsedBy : "",
                  }))
                }
                options={PAYMENT_METHOD_OPTIONS}
              />
            </label>
            {documentForm.paymentMethod === "UPI" ? (
              <>
                <label className="finance-expenses-field">
                  <span className="finance-expenses-field__label">UPI ID (optional)</span>
                  <input
                    type="text"
                    value={documentForm.upiId}
                    onChange={(event) =>
                      setDocumentForm((current) => ({ ...current, upiId: event.target.value }))
                    }
                    placeholder="e.g. name@upi"
                    maxLength={120}
                    className="search-input w-full !text-black"
                  />
                </label>
                <label className="finance-expenses-field finance-expenses-field--full">
                  <span className="finance-expenses-field__label">UPI Screenshot</span>
                  <input
                    ref={documentUpiScreenshotInputRef}
                    type="file"
                    accept={EXPENSE_IMAGE_ACCEPT}
                    onChange={(event) => setDocumentUpiScreenshotFile(event.target.files?.[0] || null)}
                    className="finance-expenses-file-input"
                  />
                  {documentUpiScreenshotFile ? (
                    <span className="finance-expenses-file-hint">Selected: {documentUpiScreenshotFile.name}</span>
                  ) : editingDocumentMeta?.hasUpiScreenshot ? (
                    <span className="finance-expenses-file-hint">
                      Current: {editingDocumentMeta.upiScreenshotFileName || "UPI screenshot on file"}
                    </span>
                  ) : null}
                </label>
              </>
            ) : null}
            {documentForm.paymentMethod === "Card" ? (
              <label className="finance-expenses-field">
                <span className="finance-expenses-field__label">Whose Card Was Used</span>
                <input
                  type="text"
                  value={documentForm.cardUsedBy}
                  onChange={(event) =>
                    setDocumentForm((current) => ({ ...current, cardUsedBy: event.target.value }))
                  }
                  placeholder="e.g. Employee / Company card holder"
                  maxLength={255}
                  className="search-input w-full !text-black"
                />
              </label>
            ) : null}
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Date</span>
              <input
                type="date"
                value={documentForm.expenseDate}
                onChange={(event) =>
                  setDocumentForm((current) => ({ ...current, expenseDate: event.target.value }))
                }
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Price</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={documentForm.price}
                onChange={(event) =>
                  setDocumentForm((current) => ({ ...current, price: event.target.value }))
                }
                placeholder="0.00"
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field">
              <span className="finance-expenses-field__label">Paid By</span>
              <input
                type="text"
                value={documentForm.paidBy}
                onChange={(event) =>
                  setDocumentForm((current) => ({ ...current, paidBy: event.target.value }))
                }
                placeholder="e.g. Company / Employee name"
                maxLength={255}
                className="search-input w-full !text-black"
              />
            </label>
            <label className="finance-expenses-field finance-expenses-field--full">
              <span className="finance-expenses-field__label">Upload Invoice</span>
              <input
                ref={documentInvoiceInputRef}
                type="file"
                accept={EXPENSE_FILE_ACCEPT}
                onChange={(event) => setDocumentInvoiceFile(event.target.files?.[0] || null)}
                className="finance-expenses-file-input"
              />
              {documentInvoiceFile ? (
                <span className="finance-expenses-file-hint">Selected: {documentInvoiceFile.name}</span>
              ) : editingDocumentMeta?.hasInvoice ? (
                <span className="finance-expenses-file-hint">
                  Current: {editingDocumentMeta.invoiceFileName || "Invoice on file"}
                </span>
              ) : null}
            </label>
            <label className="finance-expenses-field finance-expenses-field--full">
              <span className="finance-expenses-field__label">Description</span>
              <textarea
                value={documentForm.description}
                onChange={(event) =>
                  setDocumentForm((current) => ({ ...current, description: event.target.value }))
                }
                placeholder="Add document details"
                rows={3}
                maxLength={2000}
                className="search-input w-full !text-black resize-y min-h-[80px]"
              />
            </label>
          </div>
          <div className="finance-expenses-form__actions">
            <button
              type="button"
              className="finance-action-btn"
              onClick={resetDocumentForm}
              disabled={documentSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="finance-action-btn finance-action-btn--primary"
              disabled={documentSaving}
            >
              {documentSaving ? "Saving…" : editingDocumentId ? "Update document" : "Save document"}
            </button>
          </div>
        </form>
      ) : null}

      {documentsLoading ? (
        <div className="finance-expenses-loading">
          <Spin />
        </div>
      ) : financeDocuments.length ? (
        <div className="overflow-x-auto">
          <table className="finance-expenses-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Date</th>
                <th>Price</th>
                <th>Payment</th>
                <th>Paid By</th>
                <th>Description</th>
                <th>Attachments</th>
                <th>Added On</th>
                {canCreateDocuments || canDeleteDocuments ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {financeDocuments.map((document) => (
                <tr key={document.id}>
                  <td>
                    {getExpenseTypeLabel(document.expenseType, document.customExpenseType) ||
                      document.title ||
                      "—"}
                  </td>
                  <td>{formatExpenseDate(document.expenseDate)}</td>
                  <td>{formatCurrency(document.price)}</td>
                  <td>
                    <div className="finance-expenses-table__payment">
                      <span>{document.paymentMethod || "—"}</span>
                      {document.paymentMethod === "UPI" && document.upiId ? (
                        <span className="finance-expenses-table__meta">{document.upiId}</span>
                      ) : null}
                      {document.paymentMethod === "Card" && document.cardUsedBy ? (
                        <span className="finance-expenses-table__meta">{document.cardUsedBy}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>{document.paidBy || "—"}</td>
                  <td className="finance-expenses-table__reason">
                    {document.description || document.reason || "—"}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      {document.hasInvoice ? (
                        <button
                          type="button"
                          className="finance-action-btn"
                          onClick={() => handleViewDocumentAttachment(document, "invoice")}
                        >
                          Invoice
                        </button>
                      ) : null}
                      {document.hasUpiScreenshot ? (
                        <button
                          type="button"
                          className="finance-action-btn"
                          onClick={() => handleViewDocumentAttachment(document, "upiScreenshot")}
                        >
                          UPI
                        </button>
                      ) : null}
                      {!document.hasInvoice && !document.hasUpiScreenshot ? "—" : null}
                    </div>
                  </td>
                  <td>{formatDocumentDate(document.createdAt)}</td>
                  {canCreateDocuments || canDeleteDocuments ? (
                    <td>
                      <div className="flex flex-wrap gap-2">
                        {canCreateDocuments ? (
                          <button
                            type="button"
                            className="finance-action-btn"
                            onClick={() => openEditDocument(document)}
                          >
                            Edit
                          </button>
                        ) : null}
                        {canDeleteDocuments ? (
                          <button
                            type="button"
                            className="finance-action-btn finance-action-btn--danger"
                            onClick={() => setDocumentToDelete(document)}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-[#6f7d97] py-6 text-center">
          No documents uploaded for {getMonthLabel(filterMonth)} {filterYear}.
        </p>
      )}

      {viewingDocumentAttachment ? (
        <div className="finance-expenses-attachment-viewer">
          <div className="finance-expenses-attachment-viewer__header">
            <h3 className="text-base font-semibold text-[#27314a]">
              {viewingDocumentAttachment.attachmentType === "invoice" ? "Invoice" : "UPI Screenshot"}
            </h3>
            <button
              type="button"
              className="finance-action-btn"
              onClick={closeDocumentAttachmentViewer}
            >
              Close
            </button>
          </div>
          {documentAttachmentViewLoading ? (
            <div className="finance-expenses-loading">
              <Spin />
            </div>
          ) : null}
          {!documentAttachmentViewLoading &&
          documentAttachmentViewUrl &&
          isPdfMimeType(
            viewingDocumentAttachment.attachmentType === "invoice"
              ? viewingDocumentAttachment.document.invoiceMimeType
              : viewingDocumentAttachment.document.upiScreenshotMimeType
          ) ? (
            <div className="finance-expenses-attachment-viewer__content">
              <CompanyPolicyPdfViewer
                key={documentAttachmentViewUrl}
                fileUrl={documentAttachmentViewUrl}
              />
            </div>
          ) : null}
          {!documentAttachmentViewLoading &&
          documentAttachmentViewUrl &&
          isImageMimeType(
            viewingDocumentAttachment.attachmentType === "invoice"
              ? viewingDocumentAttachment.document.invoiceMimeType
              : viewingDocumentAttachment.document.upiScreenshotMimeType
          ) ? (
            <div className="finance-expenses-attachment-viewer__content">
              <img
                src={documentAttachmentViewUrl}
                alt={
                  viewingDocumentAttachment.attachmentType === "invoice"
                    ? "Document invoice"
                    : "UPI screenshot"
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const renderReports = () => (
    <section className="card-border card-body">
      <h2 className="text-lg font-semibold text-[#27314a] mb-4">Finance Summary Reports</h2>
      {renderFilters()}
      {renderSalaryCompanyTabs()}
      {isSheetLoading ? (
        <p className="text-sm text-[#6f7d97] py-6">Loading report…</p>
      ) : (
        <>
          <p className="text-sm text-[#6f7d97] mb-3">
            {getMonthLabel(filterMonth)} {filterYear}
            {activeCompanyLabel ? ` · ${activeCompanyLabel}` : ""}
          </p>
          {renderSummaryCards(sheetSummary)}
          {filteredEntries.length ? renderSheetTable() : (
            <p className="text-sm text-[#6f7d97] py-4">
              {searchedEntries.length
                ? `No salary data for ${activeCompanyLabel || "this company"} this month.`
                : "No salary data for this month yet."}
            </p>
          )}
          {renderSheetActionButtons()}
        </>
      )}
    </section>
  );

  const tabContent = {
    dashboard: renderDashboard,
    salary: renderSalaryManagement,
    sheets: renderMonthlySheets,
    records: renderRecords,
    deductions: renderDeductions,
    expenses: renderExpensesManagement,
    reports: renderReports,
  };

  return (
    <div className="finance-management-page">
      <CompanyDetailsFormModal
        open={companyFormOpen}
        onClose={() => setCompanyFormOpen(false)}
        onSuccess={handleCompanyCreated}
      />
      <div className="mb-4">
        <h1 className="page-title">
          {financePageSection === "finance" ? "Finance Management" : "Salary Management"}
        </h1>
        <p className="page-subtitle">
          {financePageSection === "finance"
            ? "Review finance summaries and manage monthly expenses and supporting documents."
            : `Manage employee salaries and monthly sheets from ${getMonthLabel(MANUAL_SHEET_MIN.month)} ${MANUAL_SHEET_MIN.year} through the current month. Months before ${getMonthLabel(FINANCE_SHEET_START.month)} ${FINANCE_SHEET_START.year} use manual historical sheets.`}
        </p>
      </div>

      {/* <nav className="finance-tabs" aria-label="Finance sections">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`finance-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav> */}

      {tabContent[activeTab]?.()}

      <RemoveSalaryStructureModal
        isOpen={Boolean(structureToDelete)}
        structure={structureToDelete}
        lastWorkingDate={
          structureToDelete?.lastWorkingDate ||
          lastWorkingDateByUserId.get(Number(structureToDelete?.userId)) ||
          null
        }
        onClose={() => {
          if (structureDeleteLoading) return;
          setStructureToDelete(null);
        }}
        onConfirm={confirmDeleteStructure}
        confirmLoading={structureDeleteLoading}
      />
      <ConfirmModal
        isOpen={Boolean(companyDataToClear)}
        onClose={() => {
          if (companyDataClearLoading) return;
          setCompanyDataToClear(null);
        }}
        onConfirm={confirmClearCompanyData}
        title={`Clear ${companyDataToClear?.companyLabel || "company"} data`}
        content={`Remove ${companyDataToClear?.entryCount || 0} employee row(s) for ${companyDataToClear?.companyLabel || "this company"} in ${getMonthLabel(filterMonth)} ${filterYear}? Rows for other companies on this month will stay.`}
        confirmLoading={companyDataClearLoading}
        confirmText="Clear company data"
      />
      <ConfirmModal
        isOpen={Boolean(sheetToDelete)}
        onClose={() => {
          if (sheetDeleteLoading) return;
          setSheetToDelete(null);
        }}
        onConfirm={confirmDeleteSheet}
        title="Delete entire month"
        content={
          isManualPeriod
            ? `Remove the full salary sheet for ${getMonthLabel(filterMonth)} ${filterYear}? This affects all ${activeSheet?.entries?.length || 0} employee row(s) across every company tab (The Coding Studio, Sattva Tech, VentureBoo, etc.). Rows can be restored by reopening this month.`
            : `Delete the salary sheet for ${getMonthLabel(filterMonth)} ${filterYear}? This permanently removes all ${activeSheet?.entries?.length || 0} employee row(s) across every company tab.`
        }
        confirmLoading={sheetDeleteLoading}
        confirmText="Delete entire month"
      />
      <ConfirmModal
        isOpen={Boolean(entryToDelete)}
        onClose={() => {
          if (entryDeleteLoading) return;
          setEntryToDelete(null);
        }}
        onConfirm={confirmDeleteManualEntry}
        title="Remove Employee"
        content={`Are you sure you want to remove ${entryToDelete?.employeeName || "this employee"} from the ${getMonthLabel(filterMonth)} ${filterYear} salary sheet?`}
        confirmLoading={entryDeleteLoading}
        confirmText="Remove"
      />
      <ConfirmModal
        isOpen={Boolean(documentToDelete)}
        onClose={() => {
          if (documentDeleteLoading) return;
          setDocumentToDelete(null);
        }}
        onConfirm={confirmDeleteDocument}
        title="Delete document"
        content={`Remove "${documentToDelete?.title || "this document"}"? This cannot be undone.`}
        confirmLoading={documentDeleteLoading}
        confirmText="Delete"
      />
      <ConfirmModal
        isOpen={Boolean(expenseToDelete)}
        onClose={() => {
          if (expenseDeleteLoading) return;
          setExpenseToDelete(null);
        }}
        onConfirm={confirmDeleteExpense}
        title="Delete expense"
        content={`Remove "${expenseToDelete?.expenseName || "this expense"}"? This cannot be undone.`}
        confirmLoading={expenseDeleteLoading}
        confirmText="Delete"
      />
    </div>
  );
};

export default FinanceManagementPage;
